import { guiSurfaceHitRay } from './gui-ray.js';
import {
	Group,
	Mesh,
	SphereGeometry,
	RingGeometry,
	TorusGeometry,
	MeshBasicMaterial,
	LineBasicMaterial,
	LineLoop,
	BufferGeometry,
	DoubleSide,
	Vector3,
	Matrix4,
	Quaternion,
	Raycaster,
} from 'three';
import { isPointOnMap, getPlanetPosition, getPlanetRotation, getPlanetCollisionRadius, getSunCollisionRadius, tileDataFromPlanetByIndex, getPlanetTileCenter, getOrbitPosition } from '../hybrid/coordinates.js';
import { SunRadius, UnitPositionTypes } from '../hybrid/types.js';
import { UNIT_ORBIT_HEIGHT } from '../hybrid/constants.js';
import Planet from './planet.js';
import Unit, { abstractToScene } from './unit.js';
import { createLabel } from './labels.js';
import { Grid } from './hexagonal-grid.js';
import { hexagonWidth } from '../hybrid/coordinates.js';
import { createSunMesh, updateSunParticles } from './sun-shader.js';
import { hexDistance } from './hexagonal-grid.js';
import { PLANET_RENDERER } from './planet.js';
import { gpuGrade } from './gpu-grade.js';
import { xrSettings } from './xr-settings.js';
import { showHudNotice, refusalText } from './hud-notice.js';
const planetRadius = (p) => Math.max(1, Math.ceil((p?.size || 5) / 5));

const MAP_DETAIL_DISTANCE = 500;   // grey overview sphere ↔ next renderer
// next is a heavy per-planet renderer (baked texture-array RTs + a worker bake).
// Building the whole sector's worth at full detail at once fills VRAM (desktop)
// and loses the GL context (mobile → black). So next runs in tiers keyed on the
// zoom distance: a whole sector affords the MEDIUM tier; only when you zoom right
// in (< PLANET_HIGH_DISTANCE) does the visible handful escalate to HIGH. Panning
// at a constant zoom never changes the tier → no rebuild; only crossing a band
// boundary (zooming) rebuilds. See planetTier()/lodBand().
const PLANET_HIGH_DISTANCE = 150;  // medium ↔ high next tier
// XR: how many hex rings of sectors load around the viewer per zoom band —
// THE place to tune how much map the headset keeps loaded by default.
const XR_RINGS_OVERVIEW = 4; // above MAP_DETAIL_DISTANCE
const XR_RINGS_DETAIL = 1; // detail flight level (was 3)
const XR_RINGS_CLOSE = 0; // below distance 100: THIS sector only (user 2026-10-10: "at that zoom
                          // level only the current sector is loaded" — was 2, and shrinking never
                          // unloaded, so a whole neighbour system sat next to the planet)
// Detailed planet meshes kept for reuse after they leave view / flip to the
// overview LOD (LRU). Rebuilding one is a multi-100ms main-thread hitch.
// Small on mobile/standalone XR: each cached mesh is several MB of geometry
// and the headset pays for the memory pressure with GC stutter.
const IS_HEADSET = typeof navigator !== 'undefined' && /Pico|Quest|OculusBrowser/i.test(navigator.userAgent);
// phones/tablets only — the 2026-07-23 Adreno mitigations (low/medium tiers)
// must NOT hit the headsets: they gutted the Pico's planets (2026-09-28).
const IS_PHONE_GPU = !IS_HEADSET && typeof navigator !== 'undefined' && /Android|Mobile/i.test(navigator.userAgent);
const IS_MOBILE_GPU = IS_PHONE_GPU || IS_HEADSET; // shared: small cache, one build at a time
const PLANET_CACHE_MAX = IS_PHONE_GPU ? 2 : IS_HEADSET ? 4 : 12;
// Headset: below MAP_DETAIL_DISTANCE only planets within THIS camera→planet
// distance (main units) get a `next` build; the rest stay grey spheres until
// approached. Measured 2026-09-28 (Pico, probe): the detail flip built ALL 19
// planets of the loaded system, one per full map reload, each with its own
// 300–450 ms shader compile → 20 map loads and ~14 s of black frames.
// 450: the build starts right after the detail flip (500) for the planet ahead,
// so it is usually revealed by the time the player arrives; 300 started too late.
const HEADSET_NEXT_DISTANCE = 450;
// LOD band for a given zoom distance: 0 = grey overview sphere, 1 = medium next,
// 2 = high next. Used both to gate redraws (onControlsChange) and to pick the
// per-planet quality tier (createPlanet), so the two always agree.
function lodBand(distance) {
	if (distance > MAP_DETAIL_DISTANCE) return 0;
	if (distance < PLANET_HIGH_DISTANCE) return 2;
	return 1;
}
// next quality tier for the current zoom. Desktop: sector overview = medium,
// close-up = high. Mobile GPUs never get the heavy tiers: overview = low,
// close-up = medium (one/few next planets, no context loss).
function planetTier(distance, radius = 1) {
	// legacy renderer (phones — see client/planet.js PLANET_RENDERER): no
	// quality tiers exist; a constant tier means the LOD ladder never rebuilds
	// a planet on zoom and the cache key always matches.
	if (PLANET_RENDERER === 'legacy') return 'legacy';
	// Mobile: only a planet the camera is practically AT (< 60) gets medium —
	// at zoom-into-planet the target sits < 20 away while the system's other
	// planets orbit ≥ 60 away, so exactly one planet upgrades. Five planets
	// rebuilding to medium at once faulted the Adreno driver (see 2026-07-23).
	if (IS_PHONE_GPU) return distance < 60 ? 'medium' : 'low';
	// weak desktop GPUs (integrated Intel etc.) take the same ladder: the high-tier bake reset
	// Adelheid's Intel driver (client/gpu-grade.js)
	if (!IS_HEADSET && gpuGrade().grade === 'weak') return distance < 60 ? 'medium' : 'low';
	// Headset: 'high' here means "the ONE approached planet" — client/planet.js
	// keeps the lab's XR bundle for both and only lifts the atlas to 1024 for it
	// (lab v53 per-planet LOD). < 60: the zoom target sits < 20 away, the rest
	// of the system orbits ≥ 60 away → exactly one planet at full detail.
	// Headset: threshold in planet RADII (xrSettings tierHigh, ?tierhigh=), not absolute units — a radius-4 planet
	// fills the view at 60 units, a radius-1 one is still small there (2026-10-21).
	if (IS_HEADSET) return distance < xrSettings.get('tierHigh') * radius ? 'high' : 'low';
	const close = distance < PLANET_HIGH_DISTANCE;
	return close ? 'high' : 'medium';
}
// planetTier with hysteresis: a planet already at its close-up tier keeps it until
// 1.3× the threshold (no rebuild ping-pong at the boundary).
function planetTierFor(current, distance, radius = 1) {
	const want = planetTier(distance, radius);
	const close = planetTier(0, radius);
	const limit = IS_HEADSET ? xrSettings.get('tierHigh') * radius : IS_PHONE_GPU ? 60 : PLANET_HIGH_DISTANCE;
	if (IS_HEADSET) return (current === close && want !== close && distance < xrSettings.get('tierLow') * radius) ? close : want;
	if (current === close && want !== close && distance < limit * 1.3) return close;
	return want;
}
// DEV: ring buffer of headset tier decisions + build outcomes, read on the device
// via CDP (`window.__tierLog`) — the only way to see WHY a planet did or did not
// get its close-up tier in a VR session nobody can watch from outside.
const TIER_LOG_MAX = 300;
function tierLog(event, planet, d, extra) {
	if (typeof window === 'undefined') return;
	const log = window.__tierLog || (window.__tierLog = []);
	log.push({ t: Math.round(performance.now()), event, planet: planet?.name, size: planet?.size, d: Number.isFinite(d) ? Math.round(d) : d, mesh: planet?.mesh?.name, tier: planet?.tier, ...extra });
	if (log.length > TIER_LOG_MAX) log.splice(0, log.length - TIER_LOG_MAX);
}
const LABEL_HIDE_DISTANCE = 20;
const LABEL_OCCLUSION_EPSILON = 1;
const _worldNormal = new Vector3();
const TOUCH_PINCH_PX = 14; // finger distance change that makes a two-finger gesture a pinch (zoom)
const TOUCH_DRAG_PX = 10;  // midpoint travel that makes it a drag (rotate)
const TOUCH_TAP_PX = 10;   // farthest finger travel inside a press that still counts as a tap
const TSEL_THUMB_MS = 250;  // a first finger older than this and still (≤ TOUCH_DRAG_PX) is a "thumb"
const TSEL_WAIT_MS = 500;   // the second finger waits this long for a third before it becomes a plain pinch (350 lost real staggered arrivals on the loaded headless CPU: CDP touchStart spacing came out 430-700 ms for 150-400 ms sleeps)

// is `ancestor` (e.g. a planet's mesh) in the parent chain of `object` (e.g. one of its tile meshes)?
function isAncestor(ancestor, object) {
	for (let n = object; n; n = n.parent) {
		if (n === ancestor) return true;
	}
	return false;
}
const _tierCamPos = new Vector3(); // scratch: camera world pos for per-planet LOD
const _bandWorldPosition = new Vector3();
const _unitWorldPosition = new Vector3();
const _settlementLabelPosition = new Vector3();
const _labelPlanetCenter = new Vector3();

function sceneToAbstract({ x = 0, y = 0, z = 0 }) {
	return { x, y: z, z: y };
}

function isDescendantOf(node, ancestor) {
	while (node) {
		if (node === ancestor) return true;
		node = node.parent;
	}
	return false;
}

export class SectorMap {
	constructor(game) {
		this.grid = new Grid({ hexagonWidth });
		this.tiles = new Group();
		this.tiles.name = 'TILES';
		//this.tiles.$persistant = true;
		game.main.add(this.tiles);
		this.units = new Group();
		this.units.name = 'UNITS';
		game.main.add(this.units);
		// pixel-constant unit decorations live outside the unit meshes so
		// planet reparenting / sector cleanup can never eat them
		this.decorationsLayer = new Group();
		this.decorationsLayer.name = 'UNIT_DECORATIONS';
		game.main.add(this.decorationsLayer);
		game.onFrame(updateSunParticles);

		this.corners = {
			topLeft: { x: 0, y: 0 },
			topRight: { x: 0, y: 0 },
			bottomLeft: { x: 0, y: 0 },
			bottomRight: { x: 0, y: 0 },
		}; // the virtual (point) coordinates of the visible game board
		this.pointerVec = new Vector3(); // Vector used to calculate pointer position on the map plane
		this.sectorCoordinates = { x: 0, y: 0 }; // hex coordinates of the central hexagon (what you type in the game navigator)
		this.pointCoordinates = { x: 0, y: 0 }; // point (virtual) coordinates of the central hexagon (where meshes get actually positioned)
		this.lastDistance = 0;
		this.timeoutDrawGrid = null;
		this.controlsActive = false;
		this.ignorePointerUp = false;
		this._xrDebugTimer = 0;
		this.xrCenterSector = { x: 0, y: 0 };
		this.xrRings = 1;
		this._xrDetailMode = null;
		this.selectedUnits = [];
		this._planetCache = new Map(); // _id → { p: Planet, patchKey } (LRU)
		this._buildingPlanets = new Set(); // in-flight builds (concurrent drawGrid guard)
		this._deferredPlanets = new Map(); // _id → { planet, distance }: serialized builds waiting their turn
		this._headsetNearestId = null; // headset: the ONE planet allowed to be built (nearest to the camera); null = no restriction
		this._planetDocs = new Map(); // _id → newest planet doc from drawMapTiles

		// DEV ONLY: ?autozoom=N tweens the camera distance down to ~8 over N
		// seconds (default 40) while pumping onControlsChange — reproduces the
		// pinch-zoom-to-planet path on remote adb devices where multitouch
		// injection is blocked (sendevent denied on the Moto). No input
		// semantics are touched; this drives the camera directly.
		const _az = typeof location !== 'undefined'
			&& new URLSearchParams(location.search).get('autozoom');
		if (_az != null && _az !== '') {
			const secs = Math.max(5, Number(_az) || 40);
			// ?azstop=60 stops the tween at that distance (default 8) — separates
			// "medium rebuilds run" (any stop < 150) from "sun shader fullscreen"
			// (stop ≈ 8) when bisecting the Adreno fault.
			const stopAt = Math.max(8, Number(new URLSearchParams(location.search).get('azstop')) || 8);
			setTimeout(() => {
				const target = game.controls?.target;
				if (!target) return;
				const d0 = this.getDistance();
				const perFrame = Math.pow(8 / d0, 1 / (secs * 60));
				const step = () => {
					const d = this.getDistance();
					if (d <= stopAt + 0.5) return;
					game.camera.position.sub(target).multiplyScalar(perFrame).add(target);
					this.onControlsChange();
					requestAnimationFrame(step);
				};
				step();
			}, 10000); // let the map settle first
		}

		// Track live touch pointers: OrbitControls' zoomSpeed means completely
		// different things per input — wheel zooms 0.95^speed per tick (4/8 is
		// calibrated there), touch pinch zooms pow(spreadRatio, speed), so the
		// same 8 turns a 2x finger spread into a 256x zoom. Pinch needs ~1.
		// A press that BEGINS on the HUD frame (thin bars, buttons, an opened page, the tooltip) belongs to the
		// HUD for its whole life: the map controls never see it, so a finger that drifts off the bar cannot pan,
		// zoom or rotate the map, and it counts for no pinch / rectangle gesture (miserit-anthar#27).
		// The controls' own pointerdown listener is replaced by a gate that asks guiSurfaceHitRay first.
		this._hudPointers = new Set();
		const controlsDown = game.controls._onPointerDown;
		if (controlsDown) {
			game.renderer.domElement.removeEventListener('pointerdown', controlsDown);
			game.renderer.domElement.addEventListener('pointerdown', e => {
				if (this.pointerBeginsOnHud(e)) { this._hudPointers.add(e.pointerId); return; }
				controlsDown(e);
			});
		}
		this.installTouchSelection();
		this._touchPointers = new Set();
		this._touchPoints = new Map(); // pointerId → { x, y } for the two-finger twist
		// pointer kind of the latest event: touch has no hover, so the hover highlight must never follow a touch ray (#50)
		const kind = e => { this._lastPointerType = e.pointerType; };
		game.container.addEventListener('pointerdown', kind, true);
		game.container.addEventListener('pointermove', kind, true);
		const dropTouchPointer = e => {
			if (e.pointerType === 'touch') this.setLabelHighlight(null); // finger up or cancelled: nothing stays lit
			if (this._hudPointers.delete(e.pointerId)) this._hudUpId = e.pointerId; // onPointerUp (next listener) skips it
			this._touchPointers.delete(e.pointerId);
			this._touchPoints.delete(e.pointerId);
			this._twoFinger = null;
			game.controls.enableRotate = true; // the mouse (right drag) and the next gesture start enabled
			this.updateZoomSpeed();
		};
		game.container.addEventListener('pointerdown', e => {
			if (e.pointerType !== 'touch' || this._hudPointers.has(e.pointerId)) return; // a press that begins on the HUD frame belongs to the frame (#27)
			// a TAP is decided by the finger's own travel, not by the controls' 'change' (a 2 px drift
			// inside a tap pans the map a hair and dropped the tap, 2026-10-18): the first finger of a
			// gesture starts it, any further finger makes the whole gesture a non-tap
			if (!this._touchPointers.size) this._touchTap = { id: e.pointerId, x: e.clientX, y: e.clientY, drift: 0, multi: false };
			else if (this._touchTap) this._touchTap.multi = true;
			this._touchPointers.add(e.pointerId);
			this._touchPoints.set(e.pointerId, { x: e.clientX, y: e.clientY });
			this._twoFinger = null;
			this.updateZoomSpeed(); // before the first pinch move, not next frame
		});
		game.container.addEventListener('pointermove', e => {
			if (e.pointerType !== 'touch' || !this._touchPoints.has(e.pointerId)) return;
			const tap = this._touchTap;
			if (tap && tap.id === e.pointerId) tap.drift = Math.max(tap.drift, Math.hypot(e.clientX - tap.x, e.clientY - tap.y));
			if (tap && tap.drift > TOUCH_TAP_PX) this.setLabelHighlight(null); // a drag is no press (#50)
			this._touchPoints.set(e.pointerId, { x: e.clientX, y: e.clientY });
			this.touchTwoFingerGate();
		});
		game.container.addEventListener('pointerup', dropTouchPointer);
		game.container.addEventListener('pointercancel', dropTouchPointer);

		game.controls.addEventListener('start', () => { this.ignorePointerUp = false; });
		game.controls.addEventListener('change', () => {
			this.controlsActive = true;
			this.ignorePointerUp = true;
			this.onControlsChange();
		});
		game.controls.addEventListener('end', () => { this.controlsActive = false; });
		game.container.addEventListener('contextmenu', e => e.preventDefault());
		// a lost WebGL context (driver reset, GPU hang) comes back without the planets' bakes
		game.renderer.domElement.addEventListener('webglcontextrestored', () => this.rebuildPlanetsAfterContextRestore());
		game.container.addEventListener('pointerdown', (e) => this.onPointerDown(e));
		game.container.addEventListener('pointerup', (e) => this.onPointerUp(e));
		game.container.addEventListener('pointermove', (e) => {
			if (!this.bandSelect) return;
			this.bandSelect.x1 = e.clientX;
			this.bandSelect.y1 = e.clientY;
			this.updateBandOverlay();
		});

		receiver.unitPositions = units => this.setUnitPositions(units);
		receiver.unitRemaining = units => this.setUnitRemaining(units);
		receiver.unitAttack = attacks => this.setUnitAttack(attacks);
	}

	// TOUCH SELECTION RECTANGLE (user 2026-10-21): a plain pinch stays zoom; a pinch whose first
	// finger (the "thumb") is already held (≥ TSEL_THUMB_MS, ≤ 10 px travel) is the selection tool:
	// the two pinch fingers are the live corners of the dashed band box (same overlay as the desktop
	// shift-drag). Lifting one of them selects the own units inside (replaces the selection, or ADDS
	// when the thumb rests on an already-selected unit); lifting the thumb first cancels. The second
	// finger is held back from OrbitControls for TSEL_WAIT_MS: a third finger inside the window arms
	// the rectangle, otherwise the held pointerdown is replayed so the pinch starts as before. A
	// third finger during a running pinch is ignored. All interception is in the capture phase
	// (canvas pointerdown, window pointermove/up) so the controls never see the swallowed pointers.
	installTouchSelection() {
		const ts = this._tsel = { down: new Map(), mode: 'none', swallow: new Set(), thumb: null, p2: null, p3: null, timer: 0, band: null, additive: false };
		console.log('[touch] navigator.maxTouchPoints =', navigator.maxTouchPoints);
		const canvas = game.container;
		const stop = e => { e.stopImmediatePropagation(); e.preventDefault?.(); };
		const replay = p => {
			const ev = new PointerEvent('pointerdown', { pointerId: p.id, pointerType: 'touch', isPrimary: false, clientX: p.x, clientY: p.y, button: 0, buttons: 1, bubbles: true, cancelable: true });
			ev.__tselReplay = true;
			canvas.dispatchEvent(ev);
		};
		const endPending = () => {
			clearTimeout(ts.timer);
			ts.mode = 'none';
			replay(ts.p2); // the held second finger becomes an ordinary pinch finger
		};
		const cancelBox = () => {
			this.bandOverlay?.remove();
			this.bandOverlay = null;
			ts.band = null;
		};
		canvas.addEventListener('pointerdown', e => {
			if (e.pointerType !== 'touch' || e.__tselReplay || game.renderer.xr.isPresenting) return;
			if (this.pointerBeginsOnHud(e)) return; // a press that begins on the HUD frame belongs to the frame: no thumb, no pinch finger, no rectangle corner (#27)
			const now = performance.now();
			const p = { id: e.pointerId, x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, t: now };
			if (ts.mode === 'sel' || ts.mode === 'dead' || ts.mode === 'done') { ts.swallow.add(p.id); return stop(e); }
			if (ts.mode === 'pending') {
				if (ts.down.size === 2) { // the third finger arrives: arm the rectangle
					clearTimeout(ts.timer);
					ts.down.set(p.id, p);
					ts.p3 = p;
					ts.swallow.add(p.id).add(ts.p2.id);
					ts.mode = 'sel';
					ts.band = { x0: ts.p2.x, y0: ts.p2.y, x1: p.x, y1: p.y };
					const t = ts.thumb;
					game.setPointer?.(t.x, t.y);
					const onUnit = this.getIntersectedUnit();
					ts.additive = !!(onUnit && this.selectedUnits.includes(onUnit));
					this.ignorePointerUp = true; // the thumb's release is never a tap
					this.createBandOverlay(ts.band);
					return stop(e);
				}
				ts.swallow.add(p.id); return stop(e);
			}
			const n = ts.down.size;
			ts.down.set(p.id, p);
			if (n === 0) { ts.thumb = p; return; }
			if (n === 1) {
				const t = ts.thumb;
				if (t && now - t.t >= TSEL_THUMB_MS && Math.hypot(t.x - t.x0, t.y - t.y0) <= TOUCH_DRAG_PX) {
					ts.mode = 'pending';
					ts.p2 = p;
					if (this._touchTap) this._touchTap.multi = true; // thumb + a second finger is never a tap (its release must not order a course)
					clearTimeout(ts.timer);
					ts.timer = setTimeout(() => { if (ts.mode === 'pending') endPending(); }, TSEL_WAIT_MS);
					return stop(e);
				}
				return; // plain pinch
			}
			// a third finger during a running pinch: ignored
			ts.down.delete(p.id);
			ts.swallow.add(p.id);
			stop(e);
		}, true);
		window.addEventListener('pointermove', e => {
			if (e.pointerType !== 'touch') return;
			const p = ts.down.get(e.pointerId);
			if (p) { p.x = e.clientX; p.y = e.clientY; }
			if (ts.mode === 'sel') {
				if (p === ts.p2) { ts.band.x0 = p.x; ts.band.y0 = p.y; }
				else if (p === ts.p3) { ts.band.x1 = p.x; ts.band.y1 = p.y; }
				this.updateBandOverlay(ts.band);
				return stop(e); // the thumb and the corners never reach the controls
			}
			if (ts.swallow.has(e.pointerId)) return stop(e);
			if (ts.mode === 'pending') {
				if (ts.thumb && e.pointerId === ts.thumb.id && Math.hypot(p.x - p.x0, p.y - p.y0) > TOUCH_DRAG_PX) endPending(); // the thumb moves: it was a pinch
				else if (p && p === ts.p2) {
					if (Math.hypot(p.x - p.x0, p.y - p.y0) > TOUCH_PINCH_PX) { endPending(); } else return stop(e);
				}
			} else if (ts.mode === 'dead' || ts.mode === 'done') return stop(e);
		}, true);
		const up = e => {
			if (e.pointerType !== 'touch') return;
			const id = e.pointerId;
			const was = ts.down.get(id);
			ts.down.delete(id);
			let swallowed = ts.swallow.delete(id);
			if (swallowed && ts.mode === 'sel' && ts.thumb && id === ts.thumb.id) swallowed = false;
			if (ts.mode === 'pending') {
				if (was === ts.p2 || (ts.thumb && id === ts.thumb.id)) {
					clearTimeout(ts.timer);
					// the held finger was a tap or the thumb left: nothing happens until all fingers are up
					ts.mode = 'dead';
					if (was === ts.p2) swallowed = true; else { this.ignorePointerUp = true; ts.swallow.add(ts.p2.id); }
				}
			} else if (ts.mode === 'sel') {
				if (was === ts.p2 || was === ts.p3) {
					const band = ts.band;
					cancelBox();
					this.applyBandSelection(band, ts.additive, true);
					console.log('[touch] rectangle selected', this.selectedUnits.length, 'units');
					ts.mode = 'done';
					swallowed = true;
				} else if (ts.thumb && id === ts.thumb.id) { // thumb first: cancel, the corners are swallowed until up
					cancelBox();
					ts.mode = 'dead';
					this.ignorePointerUp = true;
				}
			}
			if (!ts.down.size && !ts.swallow.size) { ts.mode = 'none'; ts.thumb = null; }
			else if (!ts.down.size) ts.thumb = null;
			if (swallowed) stop(e);
		};
		window.addEventListener('pointerup', up, true);
		window.addEventListener('pointercancel', up, true);
	}

	// does this press start on a painted HUD surface? (the ray is set from the press itself)
	pointerBeginsOnHud(e) {
		if (game.renderer.xr.isPresenting) return false;
		game.setPointer?.(e.clientX, e.clientY);
		return !!guiSurfaceHitRay(game.raycaster);
	}

	onPointerDown(e) {
		// a fresh press is never a drag yet: the 'change' flag from a PROGRAMMATIC camera move
		// (focus.js) must not swallow the next click (user 2026-10-11: the first click after a
		// jump did nothing); a drag during this press re-arms it through controls 'change'
		this.ignorePointerUp = false;
		if (e.button !== 0) return;
		// touch: a tap is handled on pointerup as the one-button select (a drag re-arms
		// ignorePointerUp through the controls' 'change'); the ray follows the finger
		if (e.pointerType === 'touch') {
			game.setPointer?.(e.clientX, e.clientY);
			// press feedback only while the finger is down on the label (#50); a drag clears it
			this.setLabelHighlight(this._touchPointers.size === 1 ? this.linkedLabelUnderRay(true) : null);
			return;
		}
		// shift+drag = rectangle selection (shift so it can't interfere with pan)
		if (e.shiftKey && !game.renderer.xr.isPresenting) {
			this.bandSelect = { x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY };
			game.controls.enabled = false;
			this.createBandOverlay();
			return;
		}
		// the HUD and the labels (city / planet / sun names → their pages) win over units: a press
		// on them must not select the unit orbiting behind (user 2026-10-11: clicks on the city
		// label selected a unit instead — the label is UI)
		if (guiSurfaceHitRay(game.raycaster) || this.linkedLabelUnderRay(true)) return;
		const unit = this.getIntersectedUnit();
		if (!unit) return;
		this.ignorePointerUp = true;
		if (!e.ctrlKey) this.unselectUnits();
		if (!this.selectedUnits.includes(unit)) {
			this.selectedUnits.push(unit);
			unit.setSelected(true);
		}
	}

	createBandOverlay(band = this.bandSelect) {
		this.bandOverlay = document.createElement('div');
		this.bandOverlay.style.cssText =
			'position:fixed;border:1px dashed #ffcc00;background:rgba(255,204,0,0.08);pointer-events:none;z-index:10000;';
		document.body.appendChild(this.bandOverlay);
		this.updateBandOverlay(band);
	}

	updateBandOverlay(band = this.bandSelect) {
		if (!this.bandOverlay || !band) return;
		const { x0, y0, x1, y1 } = band;
		this.bandOverlay.style.left = Math.min(x0, x1) + 'px';
		this.bandOverlay.style.top = Math.min(y0, y1) + 'px';
		this.bandOverlay.style.width = Math.abs(x1 - x0) + 'px';
		this.bandOverlay.style.height = Math.abs(y1 - y0) + 'px';
	}

	applyBandSelection(band, additive, touchOwnOnly = false) {
		const minX = Math.min(band.x0, band.x1);
		const maxX = Math.max(band.x0, band.x1);
		const minY = Math.min(band.y0, band.y1);
		const maxY = Math.max(band.y0, band.y1);
		if (!additive) this.unselectUnits();
		// a shift+click without drag selects like a normal click
		if (maxX - minX < 4 && maxY - minY < 4) {
			const unit = this.getIntersectedUnit();
			if (unit && !this.selectedUnits.includes(unit)) {
				this.selectedUnits.push(unit);
				unit.setSelected(true);
			}
			return;
		}
		const canvas = game.container;
		for (const unit of game.entities.types.unit || []) {
			unit.mesh.getWorldPosition(_bandWorldPosition);
			_bandWorldPosition.project(game.camera);
			if (_bandWorldPosition.z > 1) continue; // behind the camera
			if (touchOwnOnly) { // touch rectangle: own units whose marker is not hidden behind a planet
				const rel = unit.getRelationToViewer?.();
				if (rel === 'enemy' || rel === 'ally' || rel === 'neutral') continue;
				if (unit.decorations?.object3d && !unit.decorations.object3d.visible) continue;
			}
			const sx = ((_bandWorldPosition.x + 1) / 2) * canvas.clientWidth;
			const sy = ((1 - _bandWorldPosition.y) / 2) * canvas.clientHeight;
			if (sx >= minX && sx <= maxX && sy >= minY && sy <= maxY
				&& !this.selectedUnits.includes(unit)) {
				this.selectedUnits.push(unit);
				unit.setSelected(true);
			}
		}
	}

	// the nearest VISIBLE label under game.raycaster that carries a page link (city, planet, sun).
	// A planet surface nearer along the ray wins: the planet label sits at the planet's centre and
	// the city labels on the far side are behind the surface too, so a tile click in close-up
	// opened the planet page instead of the tile (measured in VR 2026-10-11: surface 0.95 m,
	// planet label 1.95 m, city label 2.88 m along the same ray). Aim at the name outside the
	// disc → the page; aim at the planet body → the tile.
	// A unit marker under the same ray wins over the name (user-visible decision 2026-10-24, ticket 14:
	// the unit beside Lavara could not be tapped): with unitWins the label is skipped when a unit is hit.
	linkedLabelUnderRay(unitWins = false) {
		if (!game.labels) return null;
		if (unitWins && this.getIntersectedUnit()) return null;
		const tiles = game.entities.meshes.planetTiles;
		const surface = tiles ? game.raycaster.intersectObjects(tiles)[0] : null;
		const surfaceDistance = surface ? surface.distance : Infinity;
		for (const hit of game.raycaster.intersectObjects(game.labels.children, true)) {
			let n = hit.object; while (n && !n.userData?.link && n !== game.labels) n = n.parent;
			if (!n || !n.userData?.link || !n.visible) continue;
			// behind the surface: only a planet's OWN name counts (it is painted over its disc,
			// at the centre; the user aims at the visible text) — a city name on the far side does not
			if (hit.distance > surfaceDistance && !isAncestor(n.userData.labelTarget, surface.object)) continue;
			return n;
		}
		return null;
	}

	onPointerUp(e) {
		if (this._hudUpId === e.pointerId) { this._hudUpId = null; return; } // began on the HUD: never a map click
		if (this.bandSelect) {
			const band = this.bandSelect;
			this.bandSelect = null;
			game.controls.enabled = true;
			this.bandOverlay?.remove();
			this.bandOverlay = null;
			this.applyBandSelection(band, e.ctrlKey);
			return;
		}
		if (e.pointerType === 'touch') {
			// touch: a tap = one finger that never travelled more than TOUCH_TAP_PX from where it went
			// down (the farthest point of the press, so a pan that comes back is no tap) and no second
			// finger during the gesture; ignorePointerUp (controls 'change') is the MOUSE rule only
			const tap = this._touchTap;
			if (!this._touchPointers.size) this._touchTap = null; // the last finger is up: gesture over
			this.ignorePointerUp = false;
			if (!tap || tap.multi || tap.id !== e.pointerId) return;
			if (Math.max(tap.drift, Math.hypot(e.clientX - tap.x, e.clientY - tap.y)) > TOUCH_TAP_PX) return;
			// the drift's tiny pan left controlsActive set (the controls' 'end' fires after this
			// listener) — the finger is up and this press IS a tap, so the course must not be blocked
			this.controlsActive = false;
			game.setPointer?.(e.clientX, e.clientY);
			this.onOneButtonSelect();
			return;
		}
		if (!game.renderer.xr.isPresenting && this.ignorePointerUp) {
			this.ignorePointerUp = false;
			return;
		}
		if (e.button === 2) {
			this.setSelectedUnitCourse(e);
			return;
		}
		if (e.button !== 0) return;

		// The pointer is over the HUD (a bar, a button, an opened page or overlay, the order notice)
		// — not a world click, and it never changes the map selection (2026-10-21: the deselect
		// used to run first, so a click on a HUD element also dropped the selection).
		if (guiSurfaceHitRay(game.raycaster)) return;

		// clicking empty map (no unit under the pointer, no camera drag —
		// that returns above via ignorePointerUp) deselects
		if (this.selectedUnits.length && !this.getIntersectedUnit()) {
			this.unselectUnits();
		}

		// (the LCARS frame is ALWAYS in game.gui, so a "gui has content" test swallowed every click, 2026-10-11; the ray test above is the rule, same as the XR rays)

		// a city / planet / sun LABEL opens its page (the label carries the link)
		const linked = this.linkedLabelUnderRay(true);
		if (linked) { navigation.loadPage(linked.userData.link); return; }

		this.openTileUnderRay();
	}

	// ONE-BUTTON select (XR trigger, touch tap — no right button exists there; user 2026-10-12:
	// "i cant click units in XR … how to send them, there is no right click"): on a unit → select
	// it (a second press deselects); anywhere else while units are selected → their course goes
	// there (a unit, a tile, or the map plane under the ray); nothing selected → the tile / page
	// click as with the mouse. The HUD and the labels keep their priority.
	onOneButtonSelect() {
		if (guiSurfaceHitRay(game.raycaster)) return;
		const linked = this.linkedLabelUnderRay(true);
		if (linked) { navigation.loadPage(linked.userData.link); return; }
		const unit = this.getIntersectedUnit();
		if (unit) {
			if (this.selectedUnits.includes(unit)) { // a press on any selected unit clears the whole selection
				this.unselectUnits();
			} else if (this.selectedUnits.length) { // another unit while a selection exists: go at it (attack / follow)
				this.setSelectedUnitCourse(null);
			} else {
				this.selectedUnits.push(unit);
				unit.setSelected(true);
			}
			return;
		}
		if (this.selectedUnits.length) {
			this.setSelectedUnitCourse(null); // onto the map = send there
			return;
		}
		this.openTileUnderRay();
	}

	// the map-plane (y = 0) point under the current ray, for a course set without a mouse position
	pointUnderRay() {
		// the map plane is y = 0 of game.main (scaled and moved in VR): intersect in MAIN-LOCAL
		// space, not world space — the world-plane point sent a unit "backwards" in the headset
		// (user 2026-10-12). Abstract map (x, y) = main-local (x, z).
		const local = game.raycaster.ray.clone();
		const inverse = this._mainInverse || (this._mainInverse = new Matrix4());
		game.main.updateMatrixWorld(true);
		inverse.copy(game.main.matrixWorld).invert();
		local.applyMatrix4(inverse);
		let t = Math.abs(local.direction.y) < 1e-6 ? -1 : -local.origin.y / local.direction.y;
		// In VR the viewer's eye sits AT the plane's height after a planet focus (the planet is put
		// at eye level), so the plane is edge-on: the ray meets it at the eye or never. Then take
		// the point along the ray at the distance of the nearest planet (what the player looks at).
		if (t < 1) {
			const d = this.nearestPlanetDistanceLocal(local.origin);
			if (!d) return null;
			t = d;
		}
		const p = local.origin.clone().addScaledVector(local.direction, t);
		return { x: p.x, y: p.z, z: 0 };
	}

	// main-local distance from `fromLocal` to the nearest planet's centre (null without planets)
	nearestPlanetDistanceLocal(fromLocal) {
		let best = null;
		const c = this._nearestPlanetTmp || (this._nearestPlanetTmp = new Vector3());
		for (const planet of game.entities.types.planet || []) {
			if (!planet.mesh) continue;
			planet.mesh.getWorldPosition(c);
			game.main.worldToLocal(c);
			const d = c.distanceTo(fromLocal);
			if (best === null || d < best) best = d;
		}
		return best;
	}

	// a settlement → its page; any other tile → the TILE page
	openTileUnderRay() {
		const meshes = game.entities.meshes.planetTiles;
		if (!meshes) return;
		const intersects = game.raycaster.intersectObjects(meshes);
		for (const intersect of intersects) {
			if (!intersect.object.data) continue;
			if (intersect.face) {
				_worldNormal.copy(intersect.face.normal).transformDirection(intersect.object.matrixWorld);
				if (_worldNormal.dot(game.raycaster.ray.direction) > 0) continue;
			}
			const { faceIndex, object, point } = intersect;
			const planetData = object.data.planet;
			// next picks by nearest tile to the world hit point (smooth sphere, no
			// per-triangle faces); legacy falls back to its per-triangle faceIndex.
			const tileIndex = object.data.$next ? object.data.$next.tileAtPoint(point) : object.data.faceIndex[faceIndex];
			const terrain = tileDataFromPlanetByIndex(planetData, tileIndex).terrain;
			// a settlement → its page; any other tile → the TILE page (the old tile dialog: facts +
			// COLONIZE when possible — user 2026-10-11: "in our lcars we don't have such modals anymore")
			if (planetData.settlements && planetData.settlements[tileIndex] && planetData.settlements[tileIndex].url) {
				navigation.loadPage(`${planetData.sector[0]}/${planetData.sector[1]}/${planetData.index}/${tileIndex}/${planetData.settlements[tileIndex].url}`);
			} else {
				navigation.loadPage(`${planetData.sector[0]}/${planetData.sector[1]}/${planetData.index}/${tileIndex}`);
			}
			break;
		}
	}

	getIntersectedUnit() {
		const units = game.entities.types.unit || [];
		const objects = units.flatMap(unit => unit.getSelectableObjects?.() || [unit.mesh]);
		if (!objects.length) return null;
		for (const object of objects) object.updateMatrixWorld?.(true);
		const intersects = game.raycaster.intersectObjects(objects, true);
		for (const intersect of intersects) {
			const unit = intersect.object.userData.unit || intersect.object.parent?.userData.unit;
			if (unit) return unit;
		}
		return null;
	}

	unselectUnits() {
		for (const unit of this.selectedUnits) unit.setSelected(false);
		this.selectedUnits = [];
	}

	async setSelectedUnitCourse(e) {
		if (!this.selectedUnits.length || this.controlsActive) return;
		const targetUnit = this.getIntersectedUnit();
		const hoveredPlanetTile = this.getHoveredPlanetTile();
		const units = [];

		for (const unit of this.selectedUnits) {
			const start = this.getUnitCourseStart(unit);
			let destination;
			let targetId;
			if (targetUnit && targetUnit !== unit) {
				destination = {
					x: targetUnit.position.x,
					y: targetUnit.position.y,
					z: targetUnit.position.z ?? 0,
				};
				targetId = targetUnit._id;
			} else if (hoveredPlanetTile) {
				destination = hoveredPlanetTile;
			} else if (e) {
				const point = this.getVirtualPoint(e.clientX, e.clientY);
				destination = { x: point.x, y: point.y, z: 0 };
			} else {
				destination = this.pointUnderRay(); // XR trigger / touch tap: no mouse position
				if (!destination) return;
			}
			units.push({ unitId: unit._id, course: [start, destination], targetId });
		}

		const response = await provider.request('game', 'units.js', 'setCourse', { units });
		// never alert(): a modal dialog blocks the page's main thread and in VR it is invisible —
		// the ray froze and nothing reacted until the tab was closed (user 2026-10-12)
		if (response?.error) {
			console.warn('[units] course refused:', response.error, response.refused || '');
			const text = refusalText(response);
			if (text) showHudNotice(game, text).catch((err) => console.warn('[units] notice failed', err));
		}
	}

	getUnitCourseStart(unit) {
		// placeUnit keeps unit.position synced to world-abstract coords for
		// orbiters too — the mesh world matrix is NOT trustworthy here (it can
		// be mid detail-zoom reparenting), so both cases read the entity state
		const p = unit.position || {};
		if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
			return { x: p.x, y: p.y, z: p.z ?? 0 };
		}
		// an orbiter whose abstract coords are not synced yet (fresh server doc after a
		// re-attach): sending `undefined` stored a null position, every distance to the unit
		// became NaN and it "fired across the solar system" (user 2026-10-12) — derive the
		// abstract (x, y) = scene (x, z) from the mesh instead
		const w = unit.mesh.getWorldPosition(new Vector3());
		game.main.worldToLocal(w);
		return { x: w.x, y: w.z, z: 0 };
	}

	getHoveredPlanetTile() {
		const meshes = game.entities.meshes.planetTiles;
		if (!meshes) return null;
		const intersects = game.raycaster.intersectObjects(meshes);
		for (const intersect of intersects) {
			if (!intersect.object.data) continue;
			if (intersect.face) {
				_worldNormal.copy(intersect.face.normal).transformDirection(intersect.object.matrixWorld);
				if (_worldNormal.dot(game.raycaster.ray.direction) > 0) continue;
			}
			const planetData = intersect.object.data.planet;
			const sunPosition = this.grid.hexToPoint(planetData.sector[0], planetData.sector[1]);
			return {
				planetIndex: planetData.index,
				tileIndex: intersect.object.data.$next ? intersect.object.data.$next.tileAtPoint(intersect.point) : intersect.object.data.faceIndex[intersect.faceIndex],
				planetType: planetData.type,
				planetSize: planetData.size,
				sector: planetData.sector,
				sunPosition,
				orbitSpeed: planetData.orbitSpeed,
				distanceToSun: planetData.distanceToSun,
				rotationSpeed: planetData.rotationSpeed,
			};
		}
		return null;
	}

	getDistance() {
		if (game.renderer.xr.isPresenting) {
			const camPos = this._xrCamPos || (this._xrCamPos = new Vector3());
			const invMatrix = this._xrInvMatrix || (this._xrInvMatrix = new Matrix4());
			game.camera.updateMatrixWorld(true);
			game.camera.getWorldPosition(camPos);
			invMatrix.copy(game.main.matrixWorld).invert();
			camPos.applyMatrix4(invMatrix);
			return Math.abs(camPos.y);
		}
		return game.controls.getDistance();
	}

	onControlsChange() {
		const isXR = game.renderer.xr.isPresenting;
		const distance = Math.round(this.getDistance());
		// The close-up tier depends on the camera→PLANET distance, but redraws only
		// fire on zoom-band crossings — zooming onto a planet inside one band left it
		// at medium forever (desktop, 2026-10-09). Re-check built planets directly.
		if (!isXR && PLANET_RENDERER !== 'legacy') {
			clearTimeout(this._tierCheckTimer);
			this._tierCheckTimer = setTimeout(() => this.updatePlanetTiers(), 250);
		}

		// Desktop only: zooming IN shrinks the visible area (zoomToCursor keeps the
		// anchor inside it), so everything needed is already loaded — skip the
		// reload unless this change CROSSES the detail threshold (LOD/payload flip).
		// Applies in overview AND in detail; without the detail half every deeper
		// zoom re-fetched the full planet payload a second time for nothing.
		// Band comparison MUST be against the last DRAWN distance, not the last
		// event: early-returned events used to overwrite lastDistance, so when the
		// debounced drawGrid fired mid-zoom-easing (still in the old band) the
		// remaining easing events all matched the new band and never rescheduled —
		// the close-up tier never built (planet stayed medium at distance 8).
		if (!isXR && distance < this.lastDistance
			&& lodBand(distance) === lodBand(this._lastDrawnDistance ?? this.lastDistance)) {
			this.lastDistance = distance;
			return;
		}
		this.lastDistance = distance;

		if (isXR) {
			const centerPoint = this.getXrMapCenterPoint();
			const centerSector = this.grid.pointToHex(centerPoint.x, centerPoint.y);
			centerSector.x += this.sectorCoordinates.x;
			centerSector.y += this.sectorCoordinates.y;
			const rings = this.getXrRingCount(distance);
			const detailMode = distance < MAP_DETAIL_DISTANCE;
			const centerChanged =
				centerSector.x !== this.xrCenterSector.x ||
				centerSector.y !== this.xrCenterSector.y;
			// Only GROWING rings need data; shrinking rings means everything the
			// smaller set needs is already loaded (descending through the ring
			// thresholds used to refetch + throw away freshly built planets).
			const ringsGrew = rings > this.xrRings;
			const detailChanged = detailMode !== this._xrDetailMode;
			if (centerChanged || ringsGrew || detailChanged) {
				this.drawGrid({ ...this.sectorCoordinates, distance });
			} else if (rings < this.xrRings) {
				// shrinking needs no data, but it DOES need the unload: zooming in used to keep
				// every sector of the overview radius alive (their planets, labels, suns)
				this.xrRings = rings;
				this.cleanUpMapTiles({ xr: true });
			} else if (IS_HEADSET && detailMode) {
				// nothing structural changed, but the headset gate (HEADSET_NEXT_DISTANCE)
				// depends on camera→planet proximity — re-evaluate the nearest ones
				this.updateHeadsetPlanetProximity(distance);
			}
			return;
		}

		const threshold = distance > MAP_DETAIL_DISTANCE
			? 50
			: distance < 100
				? 1000
				: 200;

		const corners = this.getVisiblePlaneSpaceCorners();
		const tl = corners.topLeft, tr = corners.topRight;
		const bl = corners.bottomLeft, br = corners.bottomRight;
		const otl = this.corners.topLeft, otr = this.corners.topRight;
		const obl = this.corners.bottomLeft, obr = this.corners.bottomRight;

		const moved =
			Math.hypot(tl.x - otl.x, tl.y - otl.y) > threshold ||
			Math.hypot(tr.x - otr.x, tr.y - otr.y) > threshold ||
			Math.hypot(bl.x - obl.x, bl.y - obl.y) > threshold ||
			Math.hypot(br.x - obr.x, br.y - obr.y) > threshold;

		// A LOD-band crossing must redraw even when the corners barely moved:
		// zooming 300→10 converges the corners on the anchor (way under the
		// close-zoom threshold of 1000), so `moved` alone never fired and the
		// zoomed-on planet stayed at the medium tier forever.
		const bandChanged =
			lodBand(distance) !== lodBand(this._lastDrawnDistance ?? distance);

		if (moved || bandChanged) {
			if (isXR) {
				this.drawGrid({ ...this.sectorCoordinates, distance });
			} else {
				// Desktop: reset debounce on each change event
				clearTimeout(this.timeoutDrawGrid);
				this.timeoutDrawGrid = setTimeout(() => {
					this.drawGrid(this.sectorCoordinates);
				}, 200);
			}
		}
	}

	// Headset: planets flip grey ↔ next by camera proximity (HEADSET_NEXT_DISTANCE),
	// not by the map-wide detail flip. Called every 10th XR frame from
	// onControlsChange when no sector/ring/detail change triggered a drawGrid.
	// One planet per call (builds are serialized on headsets); hysteresis on the
	// way out so a planet at the edge does not flap. Found 2026-09-28: with the
	// gate alone, flying up to a grey planet never rebuilt it (user: "close to
	// the sphere and it's grey").
	updateHeadsetPlanetProximity(distance) {
		if (this._buildingPlanets.size) return;
		game.camera.getWorldPosition(_tierCamPos);
		if (game.main) game.main.worldToLocal(_tierCamPos);
		let nearest = null, nearestD = Infinity, far = null, focus = null, focusD = Infinity, focusR = Infinity;
		for (const e of Object.values(game.entities.ids)) {
			if (!e || e.$type !== 'planet' || !e.mesh || !e.system) continue;
			const p = e.mesh.position;
			const d = Math.hypot(_tierCamPos.x - p.x, _tierCamPos.y, _tierCamPos.z - p.z);
			if (e.mesh.name !== 'sPLANET' && d > HEADSET_NEXT_DISTANCE * 1.5) { far = e; break; } // downgrade first
			if (d < nearestD) { nearest = e; nearestD = d; }
			// the planet that LOOKS closest (distance in radii) among the built ones: the only one that may take
			// the close-up tier — a radius-4 planet at 119 beats a radius-1 one at 100 (2026-10-21)
			if (e.mesh.name !== 'sPLANET' && e.tier && d / planetRadius(e) < focusR) { focus = e; focusD = d; focusR = d / planetRadius(e); }
		}
		if (nearest) this._headsetNearestId = nearest._id;
		if (far) { tierLog('downgrade-grey', far, Infinity); return this.createPlanet(this.freshPlanetDoc(far), distance); }
		// build ONLY the nearest planet, and only while it is still grey
		if (nearest && nearest.mesh.name === 'sPLANET' && nearestD < HEADSET_NEXT_DISTANCE) { tierLog('build-low', nearest, nearestD); return this.createPlanet(this.freshPlanetDoc(nearest), distance); }
		// close-up detail: the approached planet upgrades low → high (bigger atlas = more
		// mountain detail) inside 60 and falls back beyond 78 (planetTierFor hysteresis).
		// createPlanet builds-then-swaps, so the low version stays visible meanwhile.
		if (focus) {
			const want = planetTierFor(focus.tier, focusD, planetRadius(focus));
			if (want !== focus.tier) { tierLog('retier-nearest→' + want, focus, focusD); return this.createPlanet(this.freshPlanetDoc(focus), distance); }
		}
		// every OTHER built planet: drop a stale close-up tier (the Pico kept three
		// planets at 'high' 400 units away, read out 2026-10-10 — GPU memory).
		for (const e of Object.values(game.entities.ids)) {
			if (!e || e.$type !== 'planet' || e === focus || !e.mesh || e.mesh.name === 'sPLANET' || !e.tier || !e.system) continue;
			const p = e.mesh.position;
			const d = Math.hypot(_tierCamPos.x - p.x, _tierCamPos.y, _tierCamPos.z - p.z);
			const want = planetTierFor(e.tier, d, planetRadius(e));
			if (want === 'high') continue; // only the nearest planet may take the close-up tier (GPU memory)
			if (want !== e.tier) { tierLog('retier-other→' + want, e, d); return this.createPlanet(this.freshPlanetDoc(e), distance); }
		}
	}

	// Retire a Planet instance's city labels by OBJECT. Their entity ids collide
	// between tiers (`${planet}_settlement_${tile}_label`), so removing by id hit
	// whichever instance registered last — the NEW label (Pico: no city label) —
	// and skipping the removal left the old meshes in game.labels (desktop: the
	// same label 3×, 2026-10-10).
	retireSettlementLabels(inst) {
		for (const label of inst.settlementLabels || []) {
			label.traverse(o => { o.$persistant = false; }); // was parked with the cache → dispose for real now
			const entity = (game.entities.types.settlementLabel || []).find(e => e.mesh === label);
			if (entity) game.entities.remove(entity);
			else { label.parent?.remove(label); game.disposeHierarchy(label); }
		}
		inst.settlementLabels = [];
		inst.settlementLabelIds = [];
	}

	// the freshest doc for a planet entity (falls back to the entity itself)
	freshPlanetDoc(entity) {
		const doc = this._planetDocs.get(entity._id);
		if (!doc || doc === entity) return entity;
		doc.system = doc.system || entity.system;
		return doc;
	}

	// Rebuild every built planet whose tier no longer matches its camera distance
	// (build-then-swap in createPlanet; mobile/headset builds stay serialized there).
	updatePlanetTiers() {
		if (this.getDistance() > MAP_DETAIL_DISTANCE) return;
		// headset in 2D (device tests): the same nearest-only gating as in XR, so a
		// Pico 2D run exercises the XR decisions (otherwise _headsetNearestId went stale)
		if (IS_HEADSET) return this.updateHeadsetPlanetProximity(this.getDistance());
		game.camera.getWorldPosition(_tierCamPos);
		if (game.main) game.main.worldToLocal(_tierCamPos);
		for (const e of Object.values(game.entities.ids)) {
			if (!e || e.$type !== 'planet' || !e.mesh || e.mesh.name !== 'cPLANET' || !e.tier || !e.system) continue;
			const p = e.mesh.position;
			const d = Math.hypot(_tierCamPos.x - p.x, _tierCamPos.y, _tierCamPos.z - p.z);
			if (planetTierFor(e.tier, d, planetRadius(e)) !== e.tier) this.createPlanet(this.freshPlanetDoc(e), this.getDistance());
		}
	}

	// Headset: id of the planet nearest to the camera among the planets of `tiles`
	// (main-local units, same metric as the HEADSET_NEXT_DISTANCE gate).
	nearestPlanetId(tiles) {
		game.camera.getWorldPosition(_tierCamPos);
		if (game.main) game.main.worldToLocal(_tierCamPos);
		let id = null, best = Infinity;
		for (const tile of tiles) {
			if (!tile.planets) continue;
			const sys = this.grid.hexToPoint(tile.sector[0] - this.sectorCoordinates.x, tile.sector[1] - this.sectorCoordinates.y);
			for (const planet of tile.planets) {
				const o = getPlanetPosition(sys.x, sys.y, planet.orbitSpeed, planet.distanceToSun);
				const d = Math.hypot(_tierCamPos.x - o.x, _tierCamPos.y, _tierCamPos.z - o.y);
				if (d < best) { best = d; id = planet._id; }
			}
		}
		return id;
	}

	// jump the map to a sector (X/Y + GO, /x/y links, back/forward); a repeat of the current
	// sector is a no-op so SPA navigation and the frame's GO do not redraw twice
	async jumpTo(coordinates = { x: 0, y: 0 }) {
		const x = Number(coordinates.x) || 0, y = Number(coordinates.y) || 0;
		if (this._sector && this._sector.x === x && this._sector.y === y) return;
		this._sector = { x, y };
		this.sectorCoordinates = { x, y }; // the X/Y form reads it
		import('./hud-lcars.js').then(m => m.syncXyFields()).catch(() => {});
		return this.drawGrid({ x, y });
	}

	async drawGrid(coordinates = { x: 0, y: 0 }) {
		function cap(coor, centroid, percent) {
			return coor;
			//return Math.floor((coor - centroid) * (1 - percent)) + centroid;
		}
		let distance;
		if (coordinates.distance) {
			distance = coordinates.distance;
		} else {
			distance = this.getDistance();
		}
		this.lastDistance = distance;
		this._lastDrawnDistance = distance; // band anchor for onControlsChange's skip gate
		this.sectorCoordinates = coordinates;
		this.pointCoordinates = this.grid.hexToPoint(coordinates.x, coordinates.y);
		const corners = this.getVisiblePlaneSpaceCorners();
		this.corners = corners;
		if (distance < 150) {
			const max = 100;
			const edges = {
				top: Math.abs(corners.topRight.x - corners.topLeft.x),
				bottom: Math.abs(corners.bottomRight.x - corners.bottomLeft.x),
				left: Math.abs(corners.bottomLeft.y - corners.topLeft.y),
				right: Math.abs(corners.bottomRight.y - corners.topRight.y),
			};
			//l('>>>edges', edges);
			const centroid = {
				x:
					(corners.topLeft.x +
						corners.topRight.x +
						corners.bottomRight.x +
						corners.bottomLeft.x) /
					4,
				y:
					(corners.topLeft.y +
						corners.topRight.y +
						corners.bottomRight.y +
						corners.bottomLeft.y) /
					4,
			};
			//l('>>>centroid', centroid);
			const biggest = Object.entries(edges).sort((a, b) => {
				return a[1] > b[1];
			})[3][1];
			//l('>>>biggest', biggest);
			const percent = max / biggest;
			if (percent < 1) {
				//l('>>>shrink percent', percent);
				corners.topLeft.x = cap(corners.topLeft.x, centroid.x, percent);
				corners.topRight.x = cap(corners.topRight.x, centroid.x, percent);
				corners.bottomLeft.x = cap(corners.bottomLeft.x, centroid.x, percent);
				corners.bottomRight.x = cap(corners.bottomRight.x, centroid.x, percent);
				corners.topLeft.y = cap(corners.topLeft.y, centroid.y, percent);
				corners.topRight.y = cap(corners.topRight.y, centroid.y, percent);
				corners.bottomLeft.y = cap(corners.bottomLeft.y, centroid.y, percent);
				corners.bottomRight.y = cap(corners.bottomRight.y, centroid.y, percent);
			}
			//l('>>>drawGrid2 corners', corners.topLeft.x + '|' + corners.topLeft.y, corners.topRight.x + '|' + corners.topRight.y);
			//l('>>>drawGrid2 corners', corners.bottomLeft.x + '|' + corners.bottomLeft.y, corners.bottomRight.x + '|' + corners.bottomRight.y);
		}
		//this.drawVisiblePlaneSpace();

		if (game.renderer.xr.isPresenting) {
			const centerPoint = this.getXrMapCenterPoint();
			const centerSector = this.grid.pointToHex(centerPoint.x, centerPoint.y);
			centerSector.x += coordinates.x;
			centerSector.y += coordinates.y;
			const rings = this.getXrRingCount(distance);
			this.xrCenterSector = centerSector;
			this.xrRings = rings;
			this._xrDetailMode = distance < MAP_DETAIL_DISTANCE;
			const response = await provider.request('game', 'map.js', 'getMapByCenter', {
				center: centerSector,
				rings,
				distance,
			});
			if (!response?.tilesUpdate) return;
			webSocket.sendTracking({ gameMap: { center: centerSector, rings } });
			await this.drawMapTiles(response.tilesUpdate, { xr: true });
			await this.setUnitPositions(response.unitPositions || []);
			return;
		}

		const topLeft = this.grid.pointToHex(corners.topLeft.x, corners.topLeft.y);
		const topRight = this.grid.pointToHex(
			corners.topRight.x,
			corners.topRight.y,
		);
		const bottomRight = this.grid.pointToHex(
			corners.bottomRight.x,
			corners.bottomRight.y,
		);
		const bottomLeft = this.grid.pointToHex(
			corners.bottomLeft.x,
			corners.bottomLeft.y,
		);

		const cornersHex = (this.cornersSectorCoors = {
			topLeft,
			topRight,
			bottomRight,
			bottomLeft,
		});
		cornersHex.topLeft.x += coordinates.x;
		cornersHex.topLeft.y += coordinates.y;
		cornersHex.topRight.x += coordinates.x;
		cornersHex.topRight.y += coordinates.y;
		cornersHex.bottomLeft.x += coordinates.x;
		cornersHex.bottomLeft.y += coordinates.y;
		cornersHex.bottomRight.x += coordinates.x;
		cornersHex.bottomRight.y += coordinates.y;

		const response = await provider.request('game', 'map.js', 'getMapByCorners', {
			corners: cornersHex,
			distance,
		});
		if (!response?.tilesUpdate) return;
		webSocket.sendTracking({ gameMap: { corners: cornersHex } });
		await this.drawMapTiles(response.tilesUpdate, {
			topLeft,
			topRight,
			bottomRight,
			bottomLeft,
		});
		await this.setUnitPositions(response.unitPositions || []);
	}

	getVisiblePlaneSpaceCorners() {
		const { renderer } = game;

		if (renderer.xr.isPresenting) {
			return this.getXrVisiblePlaneSpaceCorners();
		}

		const { container } = game;
		// we load a little more than visible to avoid empty moments
		// by adding an overlap to the browser edges
		const overlap = 200;
		const corners = {
			topLeft: this.getVirtualPoint(-overlap, -overlap),
			topRight: this.getVirtualPoint(container.clientWidth + overlap, -overlap),
			bottomRight: this.getVirtualPoint(
				container.clientWidth + overlap,
				container.clientHeight + overlap,
			),
			bottomLeft: this.getVirtualPoint(
				-overlap,
				container.clientHeight + overlap,
			),
		};
		return corners;
	}

	getXrVisiblePlaneSpaceCorners() {
		const center = this.getXrMapCenterPoint();
		const distance = this.getDistance();
		const radius = Math.max(distance * 4, 1200);

		return {
			topLeft: { x: center.x - radius, y: center.y - radius },
			topRight: { x: center.x + radius, y: center.y - radius },
			bottomRight: { x: center.x + radius, y: center.y + radius },
			bottomLeft: { x: center.x - radius, y: center.y + radius },
		};
	}

	getXrMapCenterPoint() {
		const { main } = game;
		const cameraWorldPos = this._xrCamPos || (this._xrCamPos = new Vector3());
		const invMatrix = this._xrInvMatrix || (this._xrInvMatrix = new Matrix4());
		game.camera.updateMatrixWorld(true);
		game.camera.getWorldPosition(cameraWorldPos);
		invMatrix.copy(main.matrixWorld).invert();
		cameraWorldPos.applyMatrix4(invMatrix);

		return { x: cameraWorldPos.x, y: cameraWorldPos.z };
	}

	getXrRingCount(distance) {
		if (distance > MAP_DETAIL_DISTANCE) return XR_RINGS_OVERVIEW;
		if (distance < 100) return XR_RINGS_CLOSE;
		return XR_RINGS_DETAIL;
	}

	getVirtualPoint(screenX, screenY) {
		const { pointerVec } = this;
		const { camera, container } = game;
		const x = (screenX / container.clientWidth) * 2 - 1;
		const y = -(screenY / container.clientHeight) * 2 + 1;
		const pointer = new Vector3();
		pointerVec.set(x, y, 0);
		pointerVec.unproject(camera);
		pointerVec.sub(camera.position).normalize();
		const distance = -camera.position.y / pointerVec.y; // intersect y=0 map plane
		pointer.copy(camera.position).add(pointerVec.multiplyScalar(distance));
		// Three.js x = abstract x, Three.js z = abstract y (map plane is xz)
		return { x: pointer.x, y: pointer.z };
	}

	async drawMapTiles({ tiles, distance }, cornersHex) {
		const { entities } = game;
		//console.trace('drawMapTiles', distance, cornersHex, tiles);
		if (typeof cornersHex !== 'undefined') {
			this.cleanUpMapTiles(cornersHex); // remove tiles out of sight
		}
		if (typeof distance === 'undefined') {
			distance = game.controls.getDistance();
		}
		this.sectorsCount = tiles.length;
		if (IS_HEADSET) this._headsetNearestId = this.nearestPlanetId(tiles);
		const promises = [];
		for (let i = 0, j = tiles.length; i < j; i++) {
			const tile = tiles[i];
			const localSectorCoordinates = [
				tile.sector[0] - this.sectorCoordinates.x,
				tile.sector[1] - this.sectorCoordinates.y,
			];
			tile.position = this.grid.hexToPoint(
				localSectorCoordinates[0],
				localSectorCoordinates[1],
			);
			const sectorEntities = entities.getSector(tile.sector);
			if (sectorEntities.filter(entity => entity.$type === 'tile').length) {
				//l('sector has tile', tile.sector);
			} else {
				//l('sector has no tile', tile.sector);
				promises.push(this.drawMapTile(tile));
			}

			if (tile.type) {
				this.ensureSun(tile);
			}

			if (tile.planets) {
				for (const planet of tile.planets) {
					planet.system = tile;
					// newest server doc per planet: the headset/tier paths rebuild from the
					// ENTITY, which may still be the overview doc (no settlement tiles →
					// no city border, Pico 2026-10-10) — they look the fresh one up here.
					this._planetDocs.set(planet._id, planet);
					promises.push(this.createPlanet(planet, distance));
				}
			}
		}
		await Promise.all(promises);
	}

	cleanUpMapTiles(cornersHex) {
		const { entities } = game;
		if (entities.types.tile) {
			for (let i = entities.types.tile.length - 1; i >= 0; i--) {
				const { sector } = entities.types.tile[i];
				// XR ring hysteresis comes for free: xrRings only updates when a
				// drawGrid actually runs, and shrinking ring counts don't trigger
				// one — so the alive set stays at the LAST FETCHED radius, never
				// more (a +1 margin here kept ~50% extra sectors alive on Pico).
				const ipom = game.renderer.xr.isPresenting
					? hexDistance({ x: sector[0], y: sector[1] }, this.xrCenterSector) <= this.xrRings
					: isPointOnMap(cornersHex, { x: sector[0], y: sector[1] });
				if (!ipom) {
					// rescue unit meshes out of planet groups BEFORE planets get
					// stashed/disposed; reattachOrbitUnits re-places them when the
					// sector scrolls back into view
					for (const entity of entities.getSector(sector)) {
						if (entity.$type === 'unit' && entity.mesh && entity.mesh.parent !== this.units) {
							this.units.add(entity.mesh);
						}
					}
					// stash detailed planet meshes for reuse before the sector
					// dispose — rebuilding them is the biggest load hitch
					for (const entity of entities.getSector(sector)) {
						if (entity.$type === 'planet') this.stashPlanet(entity._id);
					}
					entities.clearSector(sector, { keepUnits: true });
				}
			}
		}
	}

	// Detach a DETAILED planet mesh from the scene and park it in the LRU cache
	// instead of letting entities.remove dispose it. Nulls the meshes on the
	// planet + planetTiles entities (and drops them from the raycast lists) so
	// the subsequent remove() can't free the cached GPU resources.
	stashPlanet(id) {
		const entity = game.entities.ids[id];
		if (!entity?.$p || entity.mesh?.name !== 'cPLANET') return;
		const dropMesh = ent => {
			if (!ent?.mesh) return;
			const list = game.entities.meshes[ent.$type];
			if (list) {
				const i = list.indexOf(ent.mesh);
				if (i >= 0) { list[i] = list[list.length - 1]; list.pop(); }
			}
			ent.mesh = null;
		};
		entity.mesh.parent?.remove(entity.mesh);
		this._planetCache.delete(id); // re-insert → freshest LRU position
		this._planetCache.set(id, { p: entity.$p, patchKey: JSON.stringify(entity.patches ?? null), tier: entity.tier });
		dropMesh(entity);
		dropMesh(game.entities.ids[id + 'pt']);
		while (this._planetCache.size > PLANET_CACHE_MAX) {
			const [oldId, evicted] = this._planetCache.entries().next().value;
			this._planetCache.delete(oldId);
			evicted.p.dispose?.();   // free next's worker + RTs + onFrame closure
			game.disposeHierarchy(evicted.p.mesh);
			this.retireSettlementLabels(evicted.p); // its parked city labels too
		}
	}

	drawTestGrid(radius = 5) {
		for (let q = -radius; q <= radius; q++) {
			for (let r = -radius; r <= radius; r++) {
				if (Math.abs(q + r) > radius) continue;
				const position = this.grid.hexToPoint(q, r);
				this.drawMapTile({
					sector: [q, r],
					position,
				});
			}
		}
	}

	async createPlanet(planet, distance) {
		// DEV ONLY: ?noplanets — skip planets entirely (control test: the sun
		// worked fullscreen on phones for months pre-next; isolate whether the
		// Adreno fault needs the planets in the frame at all).
		if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('noplanets')) return;
		planet.$type = 'planet';
		planet.r = getPlanetCollisionRadius(planet);
		planet.distance = distance;
		// current orbital position — meshes used to spawn at (0,0,0) until the
		// next movePlanets() frame (one-frame flash at the sun; permanent if the
		// tab is hidden, since the animation loop is suspended there)
		const orbitPos = getPlanetPosition(
			planet.system.position.x, planet.system.position.y,
			planet.orbitSpeed, planet.distanceToSun,
		);
		if (!game.entities.ids[planet._id]) {
			// orbital ring
			const orbit = {
				$type: 'orbit',
				$collisions: false,
				sector: planet.sector,
				mesh: new Mesh(
					new RingGeometry(planet.distanceToSun * 5 - 0.25, planet.distanceToSun * 5 + 0.25, 64),
					new MeshBasicMaterial({
						color: 0xffffff,
						side: DoubleSide,
						opacity: 0.2,
						transparent: true,
					}),
				),
			};
			orbit.mesh.rotation.x = -Math.PI / 2; // y-up: lay flat on xz
			orbit.mesh.position.set(planet.system.position.x, 0, planet.system.position.y);
			orbit.mesh.name = 'ORBIT';
			game.entities.add(orbit);
			this.tiles.add(orbit.mesh);
		}
		// camera→planet distance in main-local units (in XR `main` is scaled and
		// offset under the cameraRig; identity in 2D). Used for the per-planet
		// tier AND, on headsets, for the grey-vs-next decision below.
		game.camera.getWorldPosition(_tierCamPos);
		if (game.main) game.main.worldToLocal(_tierCamPos);
		const planetDist = Math.hypot(
			_tierCamPos.x - orbitPos.x, _tierCamPos.y, _tierCamPos.z - orbitPos.y);
		// headset: only the planet NEAREST to the camera is ever built (the system's
		// other planets are < HEADSET_NEXT_DISTANCE away too — 6 builds in a row
		// froze the Pico for ~16 s, measured 2026-10-07), and only within the gate.
		const overview = distance > MAP_DETAIL_DISTANCE
			|| (IS_HEADSET && (planetDist > HEADSET_NEXT_DISTANCE
				|| (this._headsetNearestId != null && planet._id !== this._headsetNearestId)));
		if (overview) {
			if (game.entities.ids[planet._id]) {
				// was detailed (or: same object, distance already overwritten above
				// — the mesh name is the reliable "what is showing" signal)
				if (game.entities.ids[planet._id].mesh?.name === 'cPLANET') {
					this.detachOrbitUnits(planet);
					// the detailed instance goes to the cache WITH its city labels: detach them
					// without disposing (a disposed text mesh came back EMPTY on restore —
					// user, Pico 2026-10-10); retire/disposeSettlementLabels dispose for real
					for (const id of planet.settlementLabelIds || []) {
						const e = game.entities.ids[id];
						if (!e) continue;
						if (e.mesh) { e.mesh.traverse(o => { o.$persistant = true; }); e.mesh.parent?.remove(e.mesh); }
						game.entities.remove(e);
					}
					this.stashPlanet(planet._id); // keep the detailed mesh for the way back
					// the doc becomes the GREY entity: drop the detailed state it carried, or the
					// "already at the wanted tier" check below fires on a grey sphere and the
					// planet never rebuilds on the way back (Pico, in-VR log 2026-10-10: 14×
					// build-low decided, 0 builds — the "stays grey" since the first report)
					const was = game.entities.ids[planet._id];
					if (was !== planet) { was.$p = null; was.renderer = null; was.tier = null; }
					planet.$p = null; planet.renderer = null; planet.tier = null;
					game.entities.remove(planet._id);
					game.entities.remove(planet._id + 'rg');
					game.entities.remove(planet._id + 'pt');
					game.entities.remove(planet._id + '_label');
				} else {
					return;
				}
			}
			planet.mesh = new Group();
			const planetMesh = new Mesh(
				new SphereGeometry(Math.ceil(planet.size / 5), 36, 36),
				new MeshBasicMaterial({ color: 0xcccccc }),
			);
			planet.mesh.add(planetMesh);
			planet.mesh.name = 'sPLANET';
			planet.units = new Group();
			planet.mesh.add(planet.units);
			planet.mesh.position.set(orbitPos.x, 0, orbitPos.y);
			planet.position = planet.mesh.position;
			game.entities.add(planet);
			this.tiles.add(planet.mesh);
			this.createPlanetLabel(planet); // grey planets carry their name too (user, Pico 2026-10-10)
			await this.reattachOrbitUnits(planet);
		} else {
			// Tier from the CAMERA→PLANET distance, not the global zoom: with the
			// global zoom, zooming onto ONE planet escalated EVERY planet in the
			// fetched system to the heavy high tier (measured: 6× high ≈ 800 MB GPU
			// where one was intended). The zoomed-on planet sits at ≈ the zoom
			// distance; the others are hundreds of units away and stay medium.
			const wantTier = planetTierFor(game.entities.ids[planet._id]?.tier, planetDist, planetRadius(planet));
			// Already showing this planet at the correct tier → nothing to do.
			const current = game.entities.ids[planet._id];
			if (current && current.$p && current.mesh?.name === 'cPLANET' && current.tier === wantTier) return;
			// BUILD-THEN-SWAP: the previous version (grey sphere OR the other next
			// tier) stays on screen the WHOLE time the new tier bakes. We only tear
			// it down once the replacement is ready — so a planet never blinks out
			// during a zoom-driven LOD change (the old code disposed first, leaving a
			// multi-second hole; a hung build left the planet gone forever).
			if (this._buildingPlanets.has(planet._id)) return; // a build is already in flight
			// Mobile: SERIALIZE builds — concurrent planet builds (parallel worker
			// gens + interleaved GL setup + shader compiles) faulted the Adreno
			// driver even at 1 bake/frame. One at a time; a deferred planet keeps
			// its current look (grey/old tier) and gets built DIRECTLY when the
			// in-flight build finishes (was: a full drawGrid re-kick = a server
			// round trip + a pass over every planet PER deferred build — measured
			// 20 map loads for one threshold crossing on the Pico, 2026-09-28).
			if (IS_MOBILE_GPU && this._buildingPlanets.size >= 1) {
				tierLog('defer', planet, planetDist, { want: wantTier, building: [...this._buildingPlanets].length });
				this._deferredPlanets.set(planet._id, { planet, distance });
				return;
			}
			this._deferredPlanets.delete(planet._id);
			this._buildingPlanets.add(planet._id);
			tierLog('build-start', planet, planetDist, { want: wantTier, from: current?.tier || current?.mesh?.name });
			const buildT0 = performance.now();
			let p, fromCache = false;
			try {
				const cached = this._planetCache.get(planet._id);
				const patchKey = JSON.stringify(planet.patches ?? null);
				if (cached && cached.patchKey === patchKey && cached.tier === wantTier) {
					this._planetCache.delete(planet._id);
					p = cached.p;
					fromCache = true;
					p.planet = planet; // fresh doc (settlements etc), same geometry
				} else {
					if (cached) { // stale patches or wrong tier → the cached mesh lies, rebuild
						this._planetCache.delete(planet._id);
						cached.p.dispose?.();   // free next's worker + RTs + onFrame closure
						game.disposeHierarchy(cached.p.mesh);
						this.retireSettlementLabels(cached.p);
					}
					p = await new Planet(planet, { tier: wantTier }).create();
				}
				tierLog('build-done', planet, planetDist, { want: wantTier, ms: Math.round(performance.now() - buildT0) });
			} catch (e) {
				tierLog('build-error', planet, planetDist, { want: wantTier, ms: Math.round(performance.now() - buildT0), error: String(e?.stack || e).slice(0, 200) });
				throw e;
			} finally {
				this._buildingPlanets.delete(planet._id);
				// a serialized build was deferred while this one ran — build the
				// next one directly (no map reload). Its grey-vs-next decision is
				// re-evaluated at the top of createPlanet with the CURRENT camera.
				if (this._buildingPlanets.size === 0 && this._deferredPlanets.size) {
					const [id, next] = this._deferredPlanets.entries().next().value;
					this._deferredPlanets.delete(id);
					setTimeout(() => this.createPlanet(next.planet, next.distance), 50);
				}
			}
			// Headset: the camera may have moved on while this ran (serialized builds take seconds). A build
			// the camera no longer wants is NOT swapped in (it would be re-tiered at once): a cache hit goes
			// back to the cache, a fresh bake is disposed. Logged as build-stale.
			if (IS_HEADSET) {
				game.camera.getWorldPosition(_tierCamPos);
				if (game.main) game.main.worldToLocal(_tierCamPos);
				const dNow = Math.hypot(_tierCamPos.x - orbitPos.x, _tierCamPos.y, _tierCamPos.z - orbitPos.y);
				if (planetTierFor(game.entities.ids[planet._id]?.tier, dNow, planetRadius(planet)) !== wantTier) {
					tierLog('build-stale', planet, dNow, { want: wantTier, fromCache, nearest: this._headsetNearestId === planet._id });
					if (fromCache) this._planetCache.set(planet._id, { p, patchKey: JSON.stringify(planet.patches ?? null), tier: wantTier });
					else { p.dispose?.(); game.disposeHierarchy(p.mesh); this.retireSettlementLabels(p); }
					return;
				}
			}
			// New tier is ready — NOW retire whatever was showing and swap it in.
			const old = game.entities.ids[planet._id];
			if (old) {
				this.detachOrbitUnits(planet); // rescue orbiters before the old mesh is disposed
				if (old.$p && old.$p !== p) { // free the old next tier (grey has no $p)
					this.retireSettlementLabels(old.$p);
					old.$p.dispose();
				}
				game.entities.remove(planet._id);
				game.entities.remove(planet._id + 'pt');
				game.entities.remove(planet._id + '_label');
			}
			// (re)register the new instance's city labels: a cache restore brings
			// labels whose entities were removed at the downgrade to grey
			planet.settlementLabels = p.settlementLabels;
			planet.settlementLabelIds = p.settlementLabelIds;
			(p.settlementLabels || []).forEach((label, i) => {
				const id = p.settlementLabelIds[i];
				if (!game.entities.ids[id]) game.entities.add({ _id: id, $type: 'settlementLabel', $collisions: false, sector: planet.sector, mesh: label });
				if (label.parent !== game.labels) game.labels.add(label);
			});
			planet.tier = wantTier;
			planet.$p = p;
			planet.mesh = p.mesh;
			planet.mesh.name = 'cPLANET';
			planet.units = p.units;
			planet.renderer = p;
			planet.mesh.position.set(orbitPos.x, 0, orbitPos.y);
			// the axial rotation NOW, not at the next movePlanets frame: a fresh mesh sits at
			// rotation 0 until then, and the settlement focus poll (client/focus.js) that reads a
			// tile centre in that gap aimed 74° beside the city (user 2026-10-16, Lyria deep link)
			planet.mesh.rotation.y = getPlanetRotation(planet.rotationSpeed, Date.now());
			planet.mesh.updateMatrixWorld(true);
			planet.position = planet.mesh.position;
			game.entities.add(planet);
			this.tiles.add(planet.mesh);
			this.createPlanetLabel(planet);

			game.entities.add({
				_id: planet._id + 'pt',
				$type: 'planetTiles',
				$collisions: false,
				sector: planet.sector,
				mesh: p.planetMesh,
				data: planet,
			});
			await this.reattachOrbitUnits(planet);
		}
	}

	detachOrbitUnits(planet) {
		const units = game.entities.types.unit || [];
		for (const unit of units) {
			if (
				unit.position?.type === UnitPositionTypes.ORBIT &&
				unit.sector?.[0] === planet.sector?.[0] &&
				unit.sector?.[1] === planet.sector?.[1] &&
				unit.position.planetIndex === planet.index &&
				unit.mesh.parent &&
				unit.mesh.parent !== this.units
			) {
				// rescue the mesh BEFORE entities.remove disposes the old planet
				// hierarchy — `planet` is the fresh data object here, its .units
				// group is only assigned later, so it can't be compared against
				this.units.add(unit.mesh);
				// analytic world position — scene matrices are mid-transition here
				getOrbitPosition({ ...unit.position, time: Date.now() }).then(worldOrbit => {
					unit.position.x = worldOrbit.x;
					unit.position.y = worldOrbit.y;
					unit.position.z = worldOrbit.z;
					unit.mesh.position.copy(abstractToScene(worldOrbit));
				});
			}
		}
	}

	async reattachOrbitUnits(planet) {
		const units = game.entities.types.unit || [];
		for (const unit of units) {
			if (
				unit.position?.type === UnitPositionTypes.ORBIT &&
				unit.sector?.[0] === planet.sector?.[0] &&
				unit.sector?.[1] === planet.sector?.[1] &&
				unit.position.planetIndex === planet.index
			) {
				await this.placeUnit(unit, unit);
			}
		}
	}

	updateZoomSpeed() {
		game.controls.zoomSpeed = this._touchPointers.size
			? 1
			: game.controls.getDistance() > MAP_DETAIL_DISTANCE ? 4 : 8;
	}

	update() {
		if (game.renderer.xr.isPresenting) {
			if (!this._xrInitialGridDrawn) {
				this._xrInitialGridDrawn = true;
				const distance = this.getDistance();
				this.drawGrid({ ...this.sectorCoordinates, distance });
				return;
			}
			// XR tile loading: poll for meaningful visible-area changes.
			if (!this._xrFrameCount) this._xrFrameCount = 0;
			if (++this._xrFrameCount % 10 === 0) {
				this.onControlsChange();
			}
		} else {
			this._xrInitialGridDrawn = false;
			this.updateZoomSpeed();
			game.controls.maxPolarAngle = game.controls.getDistance() > MAP_DETAIL_DISTANCE
				? Math.PI / 4
				: Math.PI;
		}
		this.movePlanets();
		this.moveUnits();
		this.updateLabels();
		// hover raycasts only when the ray moved (pointer or camera): with a high-tier planet in
		// close-up the tile raycast walks 270k triangles — every frame of an idle tab, 95 % of
		// Lisbeth's main thread and loud fans (2026-10-12)
		if (this.hoverRayMoved()) {
			this.hoverLabels();
			this.hoverPlanetTiles();
			this.hoverUnits();
		}
	}

	// a linked label (planet / sun / city name) under the ray lights up blue and, on desktop, the
	// cursor becomes a pointer (user 2026-10-12: "anthar label opens the planet dialog but does
	// not react to the mouse, no hover, no click visual"). One place for mouse and controller.
	hoverLabels() {
		// touch has no hover (#50): the ray follows the finger, but a label lights only while pressed
		// (setLabelHighlight from pointerdown, cleared at pointerup / cancel / drag)
		if (this._lastPointerType === 'touch' && !game.renderer.xr.isPresenting) return;
		this.setLabelHighlight(this.linkedLabelUnderRay(true));
	}

	setLabelHighlight(label) {
		if (label === this._hoveredLabel) return;
		if (this._hoveredLabel) this._hoveredLabel.setBackgroundColor(0, 0, 0, 0.66);
		if (label) label.setBackgroundColor(0.1, 0.35, 0.8, 0.9);
		this._hoveredLabel = label;
		if (!game.renderer.xr.isPresenting) game.container.style.cursor = label && this._lastPointerType !== 'touch' ? 'pointer' : '';
	}

	// Two fingers: moving TOGETHER rotates, spreading zooms (user 2026-10-12: "why not just two
	// fingers that don't pinch"). The controls do both (TOUCH.DOLLY_ROTATE), but they also rotated
	// from the midpoint drift of every uneven pinch — so a gesture is classified once at its start:
	// the fingers' distance changes by more than TOUCH_PINCH_PX before the midpoint moves by
	// TOUCH_DRAG_PX → a pinch, rotation off for the rest of the gesture; the other way round →
	// a drag, rotation on. One finger pans as before.
	touchTwoFingerGate() {
		if (this._touchPoints.size !== 2) return;
		const [a, b] = [...this._touchPoints.values()];
		const spread = Math.hypot(b.x - a.x, b.y - a.y);
		const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
		if (!this._twoFinger) {
			this._twoFinger = { spread, mid, kind: null };
			game.controls.enableRotate = false; // undecided: a pinch must not spin the map
			return;
		}
		const g = this._twoFinger;
		if (g.kind) return;
		const spreadChange = Math.abs(spread - g.spread);
		const midMove = Math.hypot(mid.x - g.mid.x, mid.y - g.mid.y);
		if (spreadChange > TOUCH_PINCH_PX && spreadChange > midMove) {
			g.kind = 'pinch';
		} else if (midMove > TOUCH_DRAG_PX) {
			g.kind = 'rotate';
			game.controls.enableRotate = true;
		}
		if (g.kind) this.ignorePointerUp = true; // a two-finger gesture is not a tap
	}

	hoverRayMoved() {
		const ray = game.raycaster.ray;
		const last = this._lastHoverRay;
		const moved = !last || last.origin.distanceToSquared(ray.origin) > 1e-8 || last.direction.distanceToSquared(ray.direction) > 1e-10;
		if (moved) {
			if (!last) this._lastHoverRay = { origin: new Vector3(), direction: new Vector3() };
			this._lastHoverRay.origin.copy(ray.origin);
			this._lastHoverRay.direction.copy(ray.direction);
		}
		return moved;
	}

	// marker corners flash yellow under the pointer / XR controller ray
	hoverUnits() {
		const unit = this.getIntersectedUnit();
		if (unit === this._hoveredUnit) return;
		this._hoveredUnit?.setHovered(false);
		this._hoveredUnit = unit;
		unit?.setHovered(true);
	}

	async setUnitPositions(units) {
		try {
			for (const data of units) {
				let unit = game.entities.ids[data._id];
				// a NEW course (re-target) turns the nose at once; a plain re-broadcast of the same
				// course (sector change) keeps the eased heading (624ac43 "easing not stomped")
				const prevDest = unit?.destination;
				const newCourse = !!data.destination && (!prevDest || prevDest.x !== data.destination.x
					|| prevDest.y !== data.destination.y || prevDest.tileIndex !== data.destination.tileIndex);
				if (unit) {
					unit.update(data);
				} else {
					unit = new Unit(data, game);
					game.entities.add(unit);
				}
				await this.placeUnit(unit, data);
				// only orient brand-new meshes toward the course — moving units
				// steer by velocity (updateHeading); snapping the nose back to
				// the course on every broadcast stomped the easing
				if (data.destination && (!unit._lastHeadingPosition || newCourse)) unit.setDirection(data.destination);
			}
		} catch (error) {
			console.error(error);
			setTimeout(() => this.setUnitPositions(units), 500);
		}
	}

	async placeUnit(unit, data) {
		if (unit.position.type === UnitPositionTypes.ORBIT) {
			const planet = game.entities
				.getSector(unit.sector)
				.find(entity => entity.$type === 'planet' && entity.index === unit.position.planetIndex);
			if (!planet?.units) throw new Error('Unit orbit planet is not loaded yet');

			const [x, y, z] = await getPlanetTileCenter(
				unit.position.planetType,
				unit.position.planetSize,
				unit.position.tileIndex,
			);
			const localPosition = new Vector3(x, y, z);
			localPosition.add(localPosition.clone().normalize().multiplyScalar(UNIT_ORBIT_HEIGHT));
			unit.mesh.position.copy(localPosition);
			unit.setOrbitFrame(new Vector3(x, y, z));
			unit.orbit = true;
			if (unit.mesh.parent !== planet.units) planet.units.add(unit.mesh);
			// keep the abstract position in sync — parked orbiters are obstacles
			// and course starts for other sims. Computed analytically: the scene
			// graph is NOT trustworthy here (planet meshes swap in/out during
			// load and detail-zoom transitions and briefly report local coords)
			const worldOrbit = await getOrbitPosition({ ...unit.position, time: Date.now() });
			unit.position.x = worldOrbit.x;
			unit.position.y = worldOrbit.y;
			unit.position.z = worldOrbit.z;
			return;
		}

		// only a unit LEAVING an orbit drops the orbit frame; a space unit keeps its heading (resetting
		// it on every broadcast snapped the nose to +z and it eased back over ~13 frames on a re-target)
		if (unit.orbit) {
			unit.mesh.quaternion.identity();
			unit.ship.quaternion.identity();
		}
		unit.orbit = false;
		unit.position = data.position;
		unit.updatePosition();
		if (unit.mesh.parent !== this.units) this.units.add(unit.mesh);
	}

	setUnitRemaining(units) {
		for (const data of units) {
			const unit = game.entities.ids[data._id];
			if (!unit) continue;
			if (data.remaining === 'destroyed') {
				unit.explode();
			} else {
				unit.updateRemaining(data.remaining);
			}
		}
	}

	setUnitAttack(attacks) {
		for (const attack of attacks) {
			const unit = game.entities.ids[attack.unitId];
			const target = game.entities.ids[attack.targetId];
			if (!unit || !target) continue;
			unit.attacking = { target: attack.targetId };
			// flare first: this lazily creates the target's shield bubble, so
			// the beam can clamp its endpoint to the bubble surface
			target.onIncomingFire?.(unit);
			unit.execAttack(target);
		}
	}

	moveUnits() {
		const units = game.entities.types.unit;
		if (!units) return;
		const isXR = game.renderer.xr.isPresenting;
		const cam = isXR ? game.renderer.xr.getCamera() : game.camera;
		const camPos = this._decoCamPos || (this._decoCamPos = new Vector3());
		const camQuat = this._decoCamQuat || (this._decoCamQuat = new Quaternion());
		if (isXR) {
			game.camera.updateMatrixWorld(true);
			game.camera.getWorldPosition(camPos);
			game.main.worldToLocal(camPos);
		} else {
			camPos.copy(cam.position);
			camQuat.copy(cam.quaternion);
		}
		// same math as the map labels: 1 decoration unit = 1 screen pixel
		const tanHalfFov = 1 / cam.projectionMatrix.elements[5];
		const screenH = isXR
			? game.renderer.xr.getCamera().cameras[0]?.viewport?.height || 1800
			: window.innerHeight;
		for (const unit of units) {
			unit.updateDecorations(camPos, camQuat, tanHalfFov, screenH, isXR);
			// marker hidden while the unit is behind a planet (user 2026-10-12: "units rectangles
			// should be hidden when the unit is behind the planet, it's just too distracting")
			unit.decorations.object3d.visible = !this.unitBehindPlanet(unit, camPos);
			unit.render().catch(error => console.error('unit render failed', error));
		}
	}

	// is the viewer→unit segment blocked by a planet sphere (the label occluder spheres, main-local)?
	unitBehindPlanet(unit, camPosMainLocal) {
		const spheres = this._labelSpheres;
		if (!spheres || !spheres.length || !unit.mesh) return false;
		const u = this._unitOccPos || (this._unitOccPos = new Vector3());
		const d = this._unitOccDir || (this._unitOccDir = new Vector3());
		unit.mesh.getWorldPosition(u);
		game.main.worldToLocal(u);
		d.copy(u).sub(camPosMainLocal);
		const dist = d.length();
		if (dist < 1e-6) return false;
		d.divideScalar(dist);
		for (const s of spheres) {
			_labelPlanetCenter.copy(s.c);
			game.main.worldToLocal(_labelPlanetCenter).sub(camPosMainLocal);
			const t = _labelPlanetCenter.dot(d);
			if (t <= 0 || t >= dist) continue;
			const rLocal = s.r / (game.main ? game.main.scale.x : 1);
			if (_labelPlanetCenter.lengthSq() - t * t < rLocal * rLocal) return true;
		}
		return false;
	}

	updateLabels() {
		const isXR = game.renderer.xr.isPresenting;
		const cam = isXR ? game.renderer.xr.getCamera() : game.camera;
		const camPos = this._labelCamPos || (this._labelCamPos = new Vector3());
		const camQuat = this._labelCamQuat || (this._labelCamQuat = new Quaternion());
		const rigWorldPos = this._labelRigWorldPos || (this._labelRigWorldPos = new Vector3());
		const labelWorldPos = this._labelWorldPos || (this._labelWorldPos = new Vector3());
		const labelRayDir = this._labelRayDir || (this._labelRayDir = new Vector3());
		const labelRaycaster = this._labelRaycaster || (this._labelRaycaster = new Raycaster());
		const labelOccluders = this.getLabelOccluders();
		if (isXR) {
			game.cameraRig.updateMatrixWorld(true);
			game.camera.updateMatrixWorld(true);
			game.camera.getWorldPosition(camPos);
			// the viewer is the HEAD, not the rig (1.7 m below it): with the rig as origin a
			// planet name stayed on at 1.6 m and occlusion rays started under the floor (Pico 2026-10-10)
			rigWorldPos.copy(camPos);
			game.main.worldToLocal(camPos);
		} else {
			game.camera.updateMatrixWorld(true);
			game.camera.getWorldPosition(rigWorldPos);
			camPos.copy(cam.position);
			camQuat.copy(cam.quaternion);
		}
		// Derive tanHalfFov from projection matrix — works for both desktop and XR cameras
		const tanHalfFov = 1 / cam.projectionMatrix.elements[5];
		const screenH = isXR ? game.renderer.xr.getCamera().cameras[0]?.viewport?.height || 1800 : window.innerHeight;
		for (const label of game.labels.children) {
			label.getWorldPosition(labelWorldPos);
			const viewerDist = rigWorldPos.distanceTo(labelWorldPos);
			// city labels: the fixed 20-unit hide distance is several planet radii (XR scales
			// main to 0.25 → a planet is ~1 unit) — the label vanished exactly when the city
			// came into view. Hide them only closer than half their planet's radius.
			let hideDistance = LABEL_HIDE_DISTANCE;
			const labelPlanet = label.userData.planetId && game.entities.ids[label.userData.planetId];
			if (labelPlanet?.mesh) hideDistance = 0.5 * labelPlanet.mesh.getWorldPosition(_labelPlanetCenter).distanceTo(labelWorldPos);
			// planet names: the fixed 20 (world units = metres in XR) hid them 20 radii out
			// ("disappear too soon"); 2 radii kept it on while the planet filled half the view
			// ("still there this up close") — hide inside 6 radii (planet wider than ~19°)
			else if (label.userData.planetRadius) hideDistance = 2.5 * label.userData.planetRadius * (game.main ? game.main.scale.x : 1); // was 6×, 1.5×, briefly 3× (hurt VR free flight: the name is the planet page's click target): clickable when the planet is focused (≈4.2 radii), hidden at the settlement focus (≈2.2); beside an open desktop page the name may show next to the city label
			label.visible = viewerDist >= hideDistance
				&& !this.isLabelOccluded(label, rigWorldPos, labelWorldPos, viewerDist, labelOccluders, labelRaycaster, labelRayDir);
			if (!label.visible) continue;
			if (isXR) {
				label.rotation.set(0, Math.atan2(camPos.x - label.position.x, camPos.z - label.position.z), 0);
			} else {
				label.quaternion.copy(camQuat);
			}
			const dist = camPos.distanceTo(label.position);
			const scale = (2 * dist * tanHalfFov) / screenH;
			label.scale.setScalar(scale);
		}
	}

	// After a lost + restored WebGL context three re-creates programs, geometries and image
	// textures by itself, but the planets' BAKED render targets come back empty → water-only
	// planets (Adelheid 2026-10-11: "blacked out and came back water only"). Every built planet
	// is rebuilt at the tier the camera wants now; the cache is dropped, its targets are dead too.
	rebuildPlanetsAfterContextRestore() {
		for (const cached of this._planetCache.values()) {
			cached.p.dispose?.();
			game.disposeHierarchy(cached.p.mesh);
		}
		this._planetCache.clear();
		const built = (game.entities.types.planet || []).filter(e => e.$p);
		console.warn(`[sector-map] WebGL context restored: rebuilding ${built.length} planet(s)`);
		for (const entity of built) {
			entity.tier = null; // "already at this tier" must not short-circuit the rebuild
			this.createPlanet(this.freshPlanetDoc(entity), this.getDistance());
		}
	}

	getLabelOccluders() {
		const occluders = this._labelOccluders || (this._labelOccluders = []);
		occluders.length = 0;
		// planets as analytic spheres (centre + radius in world units): a detailed planet has no
		// opaque core sphere for the raycast, so names of planets BEHIND it drew over it (Pico
		// 2026-10-10, "labels in front of the planet"); the sphere test costs nothing
		const spheres = this._labelSpheres || (this._labelSpheres = []);
		spheres.length = 0;
		const mainScale = game.main ? game.main.scale.x : 1;
		for (const planet of game.entities.types.planet || []) {
			if (!planet.mesh || !planet.size) continue;
			spheres.push({ mesh: planet.mesh, c: planet.mesh.getWorldPosition(new Vector3()), r: Math.ceil(planet.size / 5) * mainScale });
		}
		// suns are spheres too: their 2520-triangle meshes (and the planets' overview spheres) made
		// up most of the 58 occluders / 110k triangles the raycaster walked PER LABEL PER FRAME —
		// 18 % of an idle desktop frame in three's triangle tests (Lisbeth profile 2026-10-11,
		// user: "the longer the game runs the stronger her fans go")
		for (const sun of game.entities.types.sun || []) {
			if (!sun.mesh) continue;
			const geometry = sun.mesh.geometry || sun.mesh.children.find(c => c.isMesh)?.geometry;
			if (!geometry) continue;
			if (!geometry.boundingSphere) geometry.computeBoundingSphere();
			const r = geometry.boundingSphere.radius * sun.mesh.getWorldScale(_labelPlanetCenter).x;
			spheres.push({ mesh: sun.mesh, c: sun.mesh.getWorldPosition(new Vector3()), r });
		}
		for (const [type, entities] of Object.entries(game.entities.types)) {
			// analytic spheres above; `planetTiles` = the detailed terrain meshes (a separate entity
			// type, 270k triangles at the high tier) — raycasting those per label per frame took
			// 95 % of Lisbeth's main thread with Anthar in close-up (2026-10-12, "fans loud again")
			if (type === 'planet' || type === 'sun' || type === 'planetTiles') continue;
			for (const entity of entities) {
				if (!entity.mesh || entity.$collisions === false || entity.$type?.endsWith('Label')) continue;
				entity.mesh.traverse(child => {
					// noLabelOcclusion: the 300k+ tri planet terrain meshes made these
					// per-label raycasts cost ~50ms/frame ON DESKTOP (worse on Pico) —
					// the planet's opaque core sphere occludes in their place for ~0ms
					if (child.userData.noLabelOcclusion) return;
					const materials = Array.isArray(child.material) ? child.material : [child.material];
					if (child.isMesh && materials.some(material => material && !material.transparent)) occluders.push(child);
				});
			}
		}
		return occluders;
	}

	isLabelOccluded(label, viewerWorldPos, labelWorldPos, labelDistance, occluders, raycaster, rayDir) {
		if (labelDistance <= LABEL_OCCLUSION_EPSILON) return false;
		rayDir.copy(labelWorldPos).sub(viewerWorldPos).normalize();
		const target = label.userData.labelTarget;
		for (const s of this._labelSpheres || []) {
			if (target && (s.mesh === target || isDescendantOf(target, s.mesh))) continue;
			_labelPlanetCenter.copy(s.c).sub(viewerWorldPos);
			const t = _labelPlanetCenter.dot(rayDir); // along the viewer→label segment
			if (t <= 0 || t >= labelDistance) continue;
			if (_labelPlanetCenter.lengthSq() - t * t < s.r * s.r) return true;
		}
		if (!occluders.length) return false;
		raycaster.set(viewerWorldPos, rayDir);
		raycaster.far = Math.max(0, labelDistance - LABEL_OCCLUSION_EPSILON);
		for (const hit of raycaster.intersectObjects(occluders, false)) {
			if (target && isDescendantOf(hit.object, target)) continue;
			return true;
		}
		return false;
	}

	// Drop the hover outline + free its GPU resources. (The old code only detached the mesh
	// and rebuilt a fresh BufferGeometry + material EVERY frame while hovering — ~60 leaked
	// GPU buffers/sec. Now the line is built once per hovered tile and disposed on change.)
	_clearHoveredTile() {
		if (this.hoveredTileLine) {
			this.hoveredTileLine.parent?.remove(this.hoveredTileLine);
			this.hoveredTileLine.geometry?.dispose();
			this.hoveredTileLine.material?.dispose();
			this.hoveredTileLine = null;
		}
		this._hoveredTileKey = null;
		this.hoveredPlanetTile = null;
	}

	hoverPlanetTiles() {
		const meshes = game.entities.meshes.planetTiles;
		if (!meshes) { this._clearHoveredTile(); return; }
		const intersects = game.raycaster.intersectObjects(meshes);
		for (const intersect of intersects) {
			if (!intersect.object.data) continue;
			// Skip back-facing hits (world-space normal vs ray direction)
			if (intersect.face) {
				_worldNormal.copy(intersect.face.normal).transformDirection(intersect.object.matrixWorld);
				if (_worldNormal.dot(game.raycaster.ray.direction) > 0) continue;
			}
			const { faceIndex, object, point } = intersect;
			const planetData = object.data.planet;
			// next picks by nearest tile to the world hit point (smooth sphere, no
			// per-triangle faces); legacy falls back to its per-triangle faceIndex.
			const tileIndex = object.data.$next ? object.data.$next.tileAtPoint(point) : object.data.faceIndex[faceIndex];
			// Same tile as last frame → the outline is already drawn; do nothing (no rebuild).
			const key = planetData.sector + ':' + planetData.index + ':' + tileIndex;
			if (key === this._hoveredTileKey) return;
			this._clearHoveredTile();
			this._hoveredTileKey = key;
			this.hoveredPlanetTile = {
				planetIndex: planetData.index,
				tileIndex,
				sector: planetData.sector,
			};
			const points = [];
			for (const bp of object.data.tiles.boundaries[tileIndex]) {
				points.push(new Vector3(Number(bp[0]), Number(bp[1]), Number(bp[2])));
			}
			const lineGeometry = new BufferGeometry();
			lineGeometry.setFromPoints(points);
			this.hoveredTileLine = new LineLoop(
				lineGeometry,
				new MeshBasicMaterial({ color: 0xffff00, opacity: 1, transparent: true }),
			);
			this.hoveredTileLine.userData = { type: 'planetTileHoverLine' };
			object.add(this.hoveredTileLine);
			return;
		}
		// No tile under the cursor this frame.
		this._clearHoveredTile();
	}

	movePlanets() {
		if (!game.entities.types.planet) return;
		for (const planet of game.entities.types.planet) {
			const pos = getPlanetPosition(
				planet.system.position.x, planet.system.position.y,
				planet.orbitSpeed, planet.distanceToSun,
			);
			// hybrid z-up → Three.js y-up: hybrid x→x, hybrid y→z
			planet.mesh.position.set(pos.x, 0, pos.y);
			planet.mesh.scale.setScalar(1);
			planet.mesh.rotation.y = getPlanetRotation(planet.rotationSpeed, Date.now());
			planet.mesh.updateMatrixWorld(true);
			// move planet label to follow orbit
			const labelEntity = game.entities.ids[planet._id + '_label'];
			if (labelEntity?.mesh) {
				labelEntity.mesh.position.set(pos.x, 0, pos.y);
			}
			if (planet.settlementLabels) {
				for (const label of planet.settlementLabels) {
					const localPosition = label.userData.planetLocalPosition;
					if (!localPosition) continue;
					_settlementLabelPosition.copy(localPosition);
					planet.mesh.localToWorld(_settlementLabelPosition);
					// game.labels lives under `main`: in XR main is scaled 0.25 + offset under
					// the rig, so a WORLD position put straight into label.position landed
					// 4× too far out — the city label never showed on the Pico (2026-10-10).
					game.labels.worldToLocal(_settlementLabelPosition);
					label.position.copy(_settlementLabelPosition);
				}
			}
		}
	}

	ensureSun(sun) {
		// DEV ONLY: ?nosun skips the sun entirely — isolates the sun's noise
		// shader when bisecting the Adreno GPU fault on phones.
		if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('nosun')) return;
		const existingSun = game.entities
			.getSector(sun.sector)
			.find(entity => entity.$type === 'sun');
		if (!existingSun) {
			this.createSun(sun);
			return;
		}
		this.ensureSunEffects(existingSun);
	}

	ensureSunEffects(sun) {
		if (!sun.mesh) return;
		let shaderLayers = 0;
		sun.mesh.traverse((child) => {
			if (child.material?.isShaderMaterial) shaderLayers++;
		});
		if (shaderLayers >= 3) return;

		for (const child of sun.mesh.children.slice()) {
			if (child.material?.isShaderMaterial) {
				game.disposeHierarchy(child);
			}
		}

		const replacement = createSunMesh(sun.type, SunRadius[sun.type] || 25);
		for (const child of replacement.children.slice()) {
			if (!child.material?.isShaderMaterial) continue;
			replacement.remove(child);
			sun.mesh.add(child);
		}
		game.disposeHierarchy(replacement);
	}

	createSun(sun) {
		sun.$type = 'sun';
		sun.r = getSunCollisionRadius(sun);
		sun.sunPosition = { x: sun.position.x, y: sun.position.y }; // preserve abstract 2D coords
		sun.mesh = createSunMesh(sun.type, SunRadius[sun.type] || 25);
		sun.mesh.position.set(sun.position.x, 0, sun.position.y);
		game.entities.add(sun);
		this.tiles.add(sun.mesh);

		const collisionRange = {
			$type: 'range',
			$collisions: false,
			sector: sun.sector,
			mesh: new Mesh(
				new TorusGeometry(sun.r, 0.025, 2, 32),
				new LineBasicMaterial({ color: 0x00ff00 }),
			),
		};
		collisionRange.mesh.rotation.x = -Math.PI / 2; // y-up: lay flat
		game.entities.add(collisionRange);
		sun.mesh.add(collisionRange.mesh);

		this.createSunLabel(sun);
	}

	async createSunLabel(sun) {
		const label = await createLabel(sun.name || '');
		label.position.set(sun.position.x, 0, sun.position.y);
		label.userData.labelTarget = sun.mesh;
		if (sun.url && sun.sector) label.userData.link = `/${sun.sector[0]}/${sun.sector[1]}/${sun.url}`; // click → the system page
		game.entities.add({
			_id: sun._id + '_label',
			$type: 'sunLabel',
			$collisions: false,
			sector: sun.sector,
			mesh: label,
		});
		game.labels.add(label);
	}

	async createPlanetLabel(planet) {
		const label = await createLabel(planet.name || '');
		label.userData.labelTarget = planet.mesh;
		label.userData.planetRadius = Math.ceil(planet.size / 5); // main-local; updateLabels hides inside 1.5× of it
		// click → the planet page (user 2026-10-11: the label must stay available to open the planet)
		if (planet.sector) label.userData.link = `/${planet.sector[0]}/${planet.sector[1]}/${planet.index}/${planet.url || ''}`;
		// initial position — movePlanets() updates it each frame
		const pos = getPlanetPosition(
			planet.system.position.x, planet.system.position.y,
			planet.orbitSpeed, planet.distanceToSun,
		);
		label.position.set(pos.x, 0, pos.y);
		game.entities.add({
			_id: planet._id + '_label',
			$type: 'planetLabel',
			$collisions: false,
			sector: planet.sector,
			mesh: label,
		});
		game.labels.add(label);
	}

	drawMapTile(tile) {
		const strokeWidth = 5;
		const mesh = new Mesh(
			new RingGeometry(
				hexagonWidth / 2 - strokeWidth,
				hexagonWidth / 2,
				6,
				1,
				Math.PI,
			),
			new MeshBasicMaterial({
				color:
					tile.nation?.[0]?.color
						? tile.nation[0].color
						: 0xbbbbbb,
				opacity: 0.75,
				side: DoubleSide,
				transparent: true,
			}),
		);
		mesh.position.set(tile.position.x, 0, tile.position.y);
		mesh.rotation.x = -Math.PI / 2; // lay flat on xz map plane
		mesh.userData.type = 'sectorTile';
		//tile.mesh.localSectorCoordinates = { x, y };
		mesh.scale.multiplyScalar(0.999);
		game.entities.add({
			...tile,
			$type: 'tile',
			$collisions: false,
			mesh,
		});
		this.tiles.add(mesh);
	}
}

export default SectorMap;
